// Shares one live Composition editor across the composer: the in-context canvas
// (in the platform preview), the slim top bar, and the opt-in layers/inspector dock
// all read/write the SAME editor via this context. Editing is ambient — there is no
// "edit mode" and no "Apply": edits autosave the document (debounced), and the flat
// master is rendered server-side at publish (see renderCompositionIfDirty).

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { debounce } from 'lodash'
import { PusherEvent } from '@mondaygpt/constants'
import { useListenToPusherEvent } from 'modules/core/hooks/useListenToPusherEvent'
import {
  bringToLife as bringToLifeApi,
  fetchComposition,
  generateBeatClip,
  regenerateReelMusic,
  renderComposition,
  renderVideoComposition,
  saveComposition,
  synthBeatVo,
} from '../../api/composition-api'
import { errorReportingService } from 'services/error-reporting/error-reporting.service'
import { editsApplyingNow } from '../../components/EditsApplyingBadge'
import { useCreativeStore } from '../../store/creative-store'
import { useGenerationStatusStore } from '../../store/generation-status-store'
import { useCreativeProcessStore } from '../../store/creative-process-store'
import { useVersionsStore } from '../../store/versions-store'
import type {
  BeatBackground,
  CarouselComposition,
  Composition,
  ImageSrc,
  PostComposition,
  VideoComposition,
  VideoBeatComposition,
  VideoLayerClip,
} from '../types'
import {
  isBackfilledComposition,
  isCarouselComposition,
  isStaticComposition,
  isVideoComposition,
} from '../types'
import {
  beatIndexAt,
  normalizeVideoComposition,
  videoBeatSpans,
  videoEditorViewDoc,
  videoTotalDuration,
  type BeatSpan,
} from '../video-time'
import { useCompositionEditor, type CompositionEditor } from './use-composition-editor'
import { useCompositionStatus } from './use-composition-status'
import {
  addSlide as opAddSlide,
  deleteSlide as opDeleteSlide,
  duplicateSlide as opDuplicateSlide,
  moveSlide as opMoveSlide,
  MAX_SLIDES,
  MIN_SLIDES,
} from './carousel-ops'
import {
  addBeat as opAddBeat,
  deleteBeat as opDeleteBeat,
  duplicateBeat as opDuplicateBeat,
  moveBeat as opMoveBeat,
  trimBeat as opTrimBeat,
  setBeatVO as opSetBeatVO,
  setMusic as opSetMusic,
  setVoVoiceId as opSetVoVoiceId,
  setBeatBackground as opSetBeatBackground,
  setBeatTransition as opSetBeatTransition,
  moveClip as opMoveClip,
  resizeClip as opResizeClip,
  setClipWindow as opSetClipWindow,
  setClipAnim as opSetClipAnim,
  removeClip as opRemoveClip,
  duplicateClip as opDuplicateClip,
  moveClipZ as opMoveClipZ,
  clipsOf,
  MAX_BEATS,
  MIN_BEATS,
} from './video-ops'

type DockState = { open: boolean; setOpen: (v: boolean) => void; openDock: () => void }

type CompositionEditorContextValue = {
  editor: CompositionEditor
  creativeId: string
  saving: boolean
  /** True when the last autosave PUT failed. The doc is still safe in memory and being
   *  retried — surfaced so the chrome can say "couldn't save" instead of failing silently. */
  saveFailed: boolean
  dock: DockState
  /** Flush any pending debounced autosave AND await the in-flight PUT (call before an AI
   *  revise so the server merges the user's latest canvas edits, not a stale snapshot). */
  flushSave: () => Promise<void>
  /** The version seq currently being PREVIEWED on the canvas (read-only look-back), or null
   *  when the live tip is shown. Preview never autosaves; only restore commits. */
  previewSeq: number | null
  /** Show a stored version on the canvas without committing (suppresses autosave + clobber). */
  previewVersion: (seq: number) => Promise<void>
  /** Leave preview and return the canvas to the live tip. */
  exitPreview: () => void
  /** Restore a version: server brings its state back as a new tip; the canvas refreshes live. */
  restoreVersion: (seq: number) => Promise<void>
}

const Ctx = createContext<CompositionEditorContextValue | null>(null)

/** The editor context, or null when the active creative has no editable composition. */
export function useComposerEditor(): CompositionEditorContextValue | null {
  return useContext(Ctx)
}

// Deck-level context for a CAROUSEL: the active-slide editor is the SAME `useComposerEditor()`
// (so every per-slide tool works unchanged), and this adds slide navigation + management.
export type CarouselDeckContextValue = {
  slides: PostComposition[]
  activeIndex: number
  selectSlide: (i: number) => void
  addSlide: () => void
  deleteSlide: (i: number) => void
  duplicateSlide: (i: number) => void
  moveSlide: (from: number, to: number) => void
  canAdd: boolean
  canDelete: boolean
}

const DeckCtx = createContext<CarouselDeckContextValue | null>(null)

/** The carousel deck context, or null for a static post / no composition. */
export function useCarouselDeck(): CarouselDeckContextValue | null {
  return useContext(DeckCtx)
}

// Timeline-level context for a VIDEO: the active-beat overlay editor is the SAME
// `useComposerEditor()` (so every per-beat text tool works unchanged), and this adds beat
// navigation + management + the EXPLICIT render trigger. Mirrors useCarouselDeck, but with the
// critical difference that editing NEVER auto-renders the master — `render()` is the only trigger.
export type VideoTimelineContextValue = {
  beats: VideoBeatComposition[]
  activeIndex: number
  selectBeat: (i: number) => void
  /** Append a beat; returns the NEW beat's id (null when at the cap) so add-intent flows
   *  (generate/animate/upload) can immediately target it. */
  addBeat: () => string | null
  deleteBeat: (i: number) => void
  duplicateBeat: (i: number) => void
  moveBeat: (from: number, to: number) => void
  trimBeat: (i: number, seconds: number) => void
  setBeatVO: (i: number, vo: string) => void
  /** Replace a beat's text-free background (uploaded clip / brand photo / swapped clip). */
  setBeatBackground: (i: number, bg: BeatBackground) => void
  /** Same, targeted by stable beat id (safe right after addBeat, before state settles). */
  setBeatBackgroundById: (beatId: string, bg: BeatBackground) => void
  /** How the beat ENTERS from the previous one (P4). No-op on beat 0. */
  setBeatTransition: (i: number, t: 'cut' | 'crossfade') => void
  // ── Free layer clips (N-series): EVERY visual layer as a deck-level clip on absolute
  // EFFECTIVE reel time. The timeline owns WHERE clips sit; content edits go through the
  // active-beat view editor (useComposerEditor) and reconcile back by layer id. ──
  clips: VideoLayerClip[]
  /** Every beat's [start,end) on the effective timeline (spans overlap by the crossfade). */
  spans: BeatSpan[]
  /** Slide a clip to a new start (length preserved). */
  moveClip: (layerId: string, newStart: number) => void
  /** Drag one edge of a clip's window. */
  resizeClip: (layerId: string, edge: 'start' | 'end', t: number) => void
  /** Set a clip's window outright (steppers / keyboard nudge). */
  setClipWindow: (layerId: string, start: number, end: number) => void
  /** Set a clip's entrance/exit preset (null clears). */
  setClipAnim: (layerId: string, dir: 'in' | 'out', preset: 'fade' | 'slide' | 'pop' | null) => void
  /** Delete a clip from the reel. */
  removeClip: (layerId: string) => void
  /** Duplicate a clip (fresh id, nudged +0.3s); returns nothing, selects via the editor. */
  duplicateClip: (layerId: string) => void
  /** Restack a clip one step up/down (track order = z order). */
  moveClipZ: (layerId: string, dir: 'up' | 'down') => void
  // ── History gesture bracket: drags that commit per pointer-move (trim, clip move/resize)
  // wrap themselves so the whole drag is ONE undo step. ──
  beginHistoryGesture: () => void
  endHistoryGesture: () => void
  /** Generate a NEW background clip for a beat (t2v from a prompt / i2v from an uploaded image) and
   *  apply it when it lands. Runs off-request (1-2 min); the beat shows in `generatingBeatIds`. */
  regenerateBeatBackground: (
    beatId: string,
    body: { shotKind: 't2v' | 'i2v'; sceneIdea?: string; imageAttachmentId?: string }
  ) => Promise<void>
  /** Beat ids whose background clip is being (re)generated right now. */
  generatingBeatIds: Set<string>
  /** Beat ids whose voiceover is being synthesized for the live preview (P1). */
  voicingBeatIds: Set<string>
  /** Synthesize ONE beat's voice now (free; Play auto-fires it for every missing voice). */
  voiceBeat: (beatId: string) => void
  /** Blueprint hydration progress ({done,total} while beats are being filmed server-side), or null. */
  hydration: { done: number; total: number } | null
  /** Start/resume hydration for a parked draft ("Bring it to life" — charges the video credit). */
  bringToLife: () => Promise<void>
  toggleMusic: () => void
  musicEnabled: boolean
  /** The locked bed (attachment/url) when hydration/render has produced one — playback plays it
   *  ducked under the VO; null until a bed exists (silent preview, bed arrives with hydration). */
  musicSrc: ImageSrc | null
  /** Lock a user-provided track (uploaded attachment) as the bed — replaces any generated one. */
  setMusicTrack: (src: ImageSrc) => void
  /** Regenerate the bed (optionally steered by a prompt); resolves when the new track lands. */
  regenerateMusic: (prompt?: string) => Promise<void>
  musicGenerating: boolean
  /** Deck-level voiceover switch (false = the reel previews/renders silent). */
  voEnabled: boolean
  setVoEnabled: (on: boolean) => void
  /** The reel's ElevenLabs voice (null = engine default). Changing re-records every beat. */
  voVoiceId: string | null
  setVoice: (voiceId: string) => void
  /** Deck-level captions switch (false = no word-timed captions in preview or render). */
  captionsEnabled: boolean
  setCaptionsEnabled: (on: boolean) => void
  /** Deck-level caption placement (K7): where word-timed captions sit in every beat. */
  captionPlacement: 'top' | 'center' | 'bottom'
  setCaptionPlacement: (p: 'top' | 'center' | 'bottom') => void
  /** Deck-level caption look (P1a): pop (bold caps) / clean (minimal) / block (accent box). */
  captionStyle: 'pop' | 'clean' | 'block'
  setCaptionStyle: (s: 'pop' | 'clean' | 'block') => void
  canAdd: boolean
  canDelete: boolean
  /** The ONLY render trigger. Autosave persists edits; this re-composites the reel master. */
  render: () => Promise<void>
  rendering: boolean
  renderError: string | null
  /** The current coarse render stage relayed live from the worker (broll/render/judge/finalize),
   *  or null when idle — drives the "Rendering — Compositing…" progress label. */
  renderPhase: string | null
  /** The persisted score reflects the last render; true when there are edits since then (K6). */
  editedSinceRender: boolean
  // ── Live inline playback (K2): a single rAF clock previews the CURRENT edit in the browser
  // (background loop/poster + word-timed captions + VO/music when present). Never renders the mp4. ──
  /** Whole-reel duration = Σ beat durations (seconds). */
  totalDuration: number
  /**
   * Playhead access is SUBSCRIPTION-BASED (not a context field): a number here would
   * re-render every context consumer at 60fps for the whole playback. Event handlers read
   * `getCurrentTime()`; render paths that must track the playhead (stage, playhead line,
   * timecode) use `useReelPlayhead(timeline)` so only THEY re-render per tick.
   */
  getCurrentTime: () => number
  subscribeTime: (cb: () => void) => () => void
  playing: boolean
  play: () => void
  pause: () => void
  /** User scrub: move the playhead + select the beat under it (pauses playback). */
  seek: (t: number) => void
}

const VideoCtx = createContext<VideoTimelineContextValue | null>(null)

const noopSubscribe = () => () => {}
const zeroTime = () => 0

/**
 * The live playhead (seconds) for render paths that must track playback — the stage, the
 * playhead line, the timecode. Backed by useSyncExternalStore over the provider's rAF
 * clock, so ONLY the subscribing component re-renders per tick (the shared context stays
 * referentially stable through a whole playback). Null-safe for static/carousel surfaces.
 */
export function useReelPlayhead(
  timeline: Pick<VideoTimelineContextValue, 'getCurrentTime' | 'subscribeTime'> | null | undefined
): number {
  return useSyncExternalStore(
    timeline?.subscribeTime ?? noopSubscribe,
    timeline?.getCurrentTime ?? zeroTime
  )
}

/** The video timeline context, or null for a static/carousel post / no composition. */
export function useVideoTimeline(): VideoTimelineContextValue | null {
  return useContext(VideoCtx)
}

const AUTOSAVE_MS = 600
// Whole-reel undo depth (matches the static editor's MAX_HISTORY in use-composition-editor).
const VIDEO_MAX_HISTORY = 50
// After a failed autosave, retry the latest unsaved doc once on this backoff. Any new edit
// in the meantime supersedes it (a newer seq), so we always retry the freshest document.
const SAVE_RETRY_MS = 2500

// Creatives whose master render was already fired this app session — by the mount
// self-heal below OR the static provider's exit render. One shared set so the two
// can never double-fire for the same creative: a reopen right after an exit render
// (server still rendering, `compositionPendingRender` still true) must not kick a
// second redundant render of the same doc.
const masterRenderFiredThisSession = new Set<string>()

// Self-heal a stuck master: the exit render is fire-and-forget, so a failed render (or
// a tab closed before it ran) leaves `compositionPendingRender` true forever — every
// master-image surface would show "Applying your edits" for a render that's never
// coming. When the composer visits the post (single active creative — grid surfaces
// deliberately never fire this), re-render the STORED doc (no body) once per creative
// per session. The static provider fires this on UNMOUNT of a view-only session (never
// concurrent with its own exit render); the deck provider fires it on mount (it has no
// exit render to race).
function selfHealStuckMasterRender(creativeId: string): void {
  if (masterRenderFiredThisSession.has(creativeId)) return
  // Dual-store check — the creative may be hydrated in either store depending
  // on which page the user came from (Home hydrates ideas, grids creatives).
  if (!editsApplyingNow(creativeId)) return
  masterRenderFiredThisSession.add(creativeId)
  renderComposition(creativeId).catch(err => {
    console.error('composition self-heal render failed:', err)
    captureRenderFailure(err, 'self_heal', creativeId)
    // Allow a later remount to retry — a permanently stuck flag is the exact
    // failure mode this heal exists for.
    masterRenderFiredThisSession.delete(creativeId)
  })
}

// The exit render is fire-and-forget, so a failure here used to be a console line
// and nothing else (the server now also stamps the terminal state and captures it
// server-side; this is the client-side half of the same family, with the step).
function captureRenderFailure(
  err: unknown,
  step: 'exit_render' | 'exit_render_retry' | 'self_heal',
  creativeId: string
): void {
  errorReportingService.captureException(err instanceof Error ? err : new Error(String(err)), {
    feature: 'composition_render',
    step,
    creativeId,
  })
}

// Compare two compositions by their VISUAL content (ignore meta.dirty + transient fields). Used by
// the master-watcher to skip a refetch+replace when the server doc equals what the client already
// holds — i.e. our own edit's echo — so a content:updated never needlessly resets the canvas.
function sameDoc(
  a: PostComposition | null | undefined,
  b: PostComposition | null | undefined
): boolean {
  if (!a || !b) return false
  const key = (c: PostComposition) =>
    JSON.stringify({
      layers: c.layers,
      canvas: c.canvas,
      brand: c.brand,
      caption: c.caption,
      hashtags: c.hashtags,
    })
  return key(a) === key(b)
}

export function CompositionEditorProvider({
  creativeId,
  enabled = true,
  children,
}: {
  creativeId: string
  /** When false (e.g. a read-only/published post), no editor mounts — static preview. */
  enabled?: boolean
  children: ReactNode
}) {
  const [composition, setComposition] = useState<Composition | null>(null)
  const [ready, setReady] = useState(false)
  const setStatus = useCompositionStatus(s => s.set)

  // Publish the loaded doc's SHAPE alongside the status. One effect (vs. per-call-site) so the
  // kind can never drift from whatever composition actually mounted below.
  useEffect(() => {
    if (!composition) return
    const kind = isVideoComposition(composition)
      ? 'video'
      : isCarouselComposition(composition)
        ? 'carousel'
        : 'static'
    useCompositionStatus.getState().setKind(creativeId, kind)
  }, [composition, creativeId])

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setReady(false)
    setComposition(null)
    setStatus(creativeId, 'loading')
    fetchComposition(creativeId)
      .then(res => {
        if (cancelled) return
        // Hydration is not an edit: normalize the STORED dirty flag away so a
        // doc left dirty by a past session can't trip the autosave effect on
        // mount (which marked editedThisSession, fired a phantom composition
        // PUT, and an exit master re-render → phantom canonical-edit
        // propagation on pure navigation). Publish still heals a truly stale
        // master server-side (renderCompositionIfDirty reads the STORED doc).
        const hydrated = res.composition
          ? { ...res.composition, meta: { ...res.composition.meta, dirty: false } }
          : null
        setComposition(hydrated)
        setReady(true)
        setStatus(creativeId, hydrated ? 'canvas' : 'none')
      })
      .catch(err => {
        if (cancelled) return
        // Non-fatal: the composer simply falls back to the static preview.
        console.warn('composition fetch failed:', err)
        setReady(true)
        setStatus(creativeId, 'none')
      })
    return () => {
      cancelled = true
    }
  }, [creativeId, enabled, setStatus])

  // Opened the composer WHILE the post is still generating? The initial fetch above runs before
  // the composition exists and returns null, so the editor never mounts and the user is stuck on
  // the static preview ("old UI"). The composition is persisted around the end of the build, so we
  // POLL for it while the post is building or just turned ready (genStatus), until it lands. A
  // single refetch keyed on imageAttachmentId used to race the composition write and give up; the
  // authoritative genStatus→'ready' signal (kept reliable by ComposerShell's reconcile poll) plus
  // a bounded retry converges instead. Legacy posts (no genStatus, no composition) never spin here.
  const genStatus = useGenerationStatusStore(s => s.statuses[creativeId]?.status)
  // A media-items backfill (the server's stand-in for a deck whose layered document has not
  // landed yet) counts as "no composition" here: the composer mounted it so the user is not
  // stuck, and keeps polling for the engine's document to swap in (seen live 2026-10-14: a
  // product-reveal deck opened straight after generation showed one "Background photo"
  // layer per slide for the rest of the session).
  const provisional = composition != null && isBackfilledComposition(composition)
  useEffect(() => {
    if (!enabled || (composition && !provisional)) return
    if (genStatus !== 'generating' && genStatus !== 'ready') return
    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    // ~21s of grace once ready (for the composition row to land), ~90s while still generating;
    // a backfilled deck waits the longer window either way (the document may be large).
    const MAX = genStatus === 'ready' && !provisional ? 14 : 60
    const poll = async () => {
      if (cancelled) return
      try {
        const res = await fetchComposition(creativeId)
        if (cancelled) return
        if (res.composition && !isBackfilledComposition(res.composition)) {
          // Same hydration-is-not-an-edit normalization as the initial load.
          setComposition({ ...res.composition, meta: { ...res.composition.meta, dirty: false } })
          setReady(true)
          setStatus(creativeId, 'canvas')
          return
        }
      } catch (err) {
        console.warn('composition post-build fetch failed:', err)
      }
      attempts += 1
      if (!cancelled && attempts < MAX) timer = setTimeout(poll, 1500)
    }
    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [genStatus, enabled, composition, provisional, creativeId, setStatus])

  // Shape flipped UNDER us in this session? An AI turn can convert a single-image post into a
  // carousel (or vice versa) — the content's mediaItems change but the loaded composition still
  // has the OLD shape, so the wrong editor (static vs deck) would stay mounted until a reload.
  // When the live content shape disagrees with the loaded composition, re-fetch (bounded poll,
  // since the new composition lands microseconds after the content:updated) and swap it in — the
  // routing below then mounts the correct inner. Guarded to only accept a matching-shape doc.
  const contentIsCarousel = useCreativeStore(s => {
    // mediaItems only exists on gallery content types (IG/FB), not e.g. TikTokVideoContent.
    const content = s.creatives.find(c => c.id === creativeId)?.content
    const items = content && 'mediaItems' in content ? content.mediaItems : undefined
    return Array.isArray(items) && items.length >= 2
  })
  // A video creative: the content carries a reel/video (videoUrl or a video attachment). Mirrors
  // contentIsCarousel so the routing below only mounts the video editor on video content, and the
  // shape-guard rejects a stale video composition on a non-video post (and vice versa).
  const contentIsVideo = useCreativeStore(s => {
    const content = s.creatives.find(c => c.id === creativeId)?.content
    if (!content) return false
    const hasVideoUrl = 'videoUrl' in content && !!content.videoUrl
    const hasVideoAttachment = 'videoAttachmentId' in content && !!content.videoAttachmentId
    const isGeneratingReel =
      'videoStatus' in content &&
      (content.videoStatus === 'generating' || content.videoStatus === 'ready')
    // A blueprint-first reel DRAFT has no videoUrl/videoStatus yet (absence IS draft) —
    // it announces itself via format:'reel' at blueprint persist and videoHydration while
    // filming. Without this, the shape-guard below rejects the video composition and the
    // storyboard editor never mounts. SCOPED to drafts (no videoStatus at all): the legacy
    // FAL path also writes format:'reel' at generation start and leaves it behind on failure
    // (videoStatus:'failed') — treating THOSE as video would unmount a working static editor.
    const record = content as unknown as Record<string, unknown>
    const isDraftReel = !record.videoStatus && (!!record.videoHydration || record.format === 'reel')
    return hasVideoUrl || hasVideoAttachment || isGeneratingReel || isDraftReel
  })
  useEffect(() => {
    if (!enabled || !ready || !composition) return
    // The loaded doc's shape agrees with the live content on BOTH axes (carousel + video) → nothing
    // to reconcile. A disagreement on either axis means the shape flipped under us; re-fetch.
    if (
      isCarouselComposition(composition) === contentIsCarousel &&
      isVideoComposition(composition) === contentIsVideo
    )
      return
    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const poll = async () => {
      if (cancelled) return
      try {
        const res = await fetchComposition(creativeId)
        if (cancelled) return
        if (
          res.composition &&
          isCarouselComposition(res.composition) === contentIsCarousel &&
          isVideoComposition(res.composition) === contentIsVideo
        ) {
          setComposition(res.composition)
          setStatus(creativeId, 'canvas')
          return
        }
      } catch (err) {
        console.warn('composition shape-change refetch failed:', err)
      }
      attempts += 1
      if (!cancelled && attempts < 8) timer = setTimeout(poll, 1200)
    }
    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [contentIsCarousel, contentIsVideo, composition, ready, enabled, creativeId, setStatus])

  // Content media REPLACED under us while NO editor is mounted? A chat generate/rebuild on a
  // legacy post (no composition, or an old-engine deck that carries a stale STATIC composition)
  // writes a fresh SocialKit composition, but neither watcher above covers it: the post-build poll
  // only runs while genStatus is generating/ready, and the shape-flip watcher bails when
  // `composition` is null and only fires when the shape actually CHANGES (a carousel→carousel
  // rebuild keeps the shape). So the new deck never loads and the user is stuck on the read-only
  // preview until a manual reload. Fix: when the content's media identity changes AND no editor is
  // mounted (composition null OR its shape disagrees with the content — both are read-only states
  // that hold no in-memory edits), bounded-poll for the freshly-written composition and swap it in.
  // When an editor IS mounted, the inner provider refreshes on the same content:updated, so we bail
  // there and never overwrite live edits.
  const mediaSignature = useCreativeStore(s => {
    const content = s.creatives.find(c => c.id === creativeId)?.content
    if (!content) return ''
    const items = content && 'mediaItems' in content ? content.mediaItems : undefined
    if (Array.isArray(items) && items.length) {
      return `c:${items.length}:${items.map(i => i.attachmentId || i.url || '').join(',')}`
    }
    // A reel's "master" is its video, not an image — key on it so a video revise (which swaps
    // videoUrl/videoAttachmentId) triggers the no-editor-mounted poll like an image revise does.
    const videoId =
      content && 'videoAttachmentId' in content ? content.videoAttachmentId : undefined
    const videoUrl = content && 'videoUrl' in content ? content.videoUrl : undefined
    if (videoId || videoUrl) return `v:${videoId || videoUrl || ''}`
    const imageId =
      content && 'imageAttachmentId' in content ? content.imageAttachmentId : undefined
    const imageUrl = content && 'imageUrl' in content ? content.imageUrl : undefined
    return `s:${imageId || imageUrl || ''}`
  })
  const lastMediaSig = useRef<string | null>(null)
  useEffect(() => {
    // Establish a baseline on the first run, and treat the "content not loaded yet -> first real
    // media" transition (empty '' -> real) as baseline too, NOT a change — otherwise a cold direct
    // navigation would fire a wasteful poll on every legacy post that will never get a composition.
    // Only a genuine in-session replacement (real -> different real) counts as a change.
    if (lastMediaSig.current === null || lastMediaSig.current === '') {
      lastMediaSig.current = mediaSignature
      return
    }
    if (mediaSignature === lastMediaSig.current) return
    lastMediaSig.current = mediaSignature
    if (!enabled) return
    // Only refetch when NO editor is actually mounted — i.e. composition is null OR its shape
    // disagrees with the live content (a stale/orphaned static composition on a carousel, which the
    // guard below renders as the read-only preview). In both states nothing holds in-memory edits, so
    // swapping in a fresh doc is safe. When an editor IS mounted (composition present AND shape
    // matches), the inner provider refreshes on the same content:updated, and refetching here could
    // clobber unsaved manual edits — so we bail.
    const editorMounted =
      !!composition &&
      isCarouselComposition(composition) === contentIsCarousel &&
      isVideoComposition(composition) === contentIsVideo
    if (editorMounted) return
    let cancelled = false
    let attempts = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const poll = async () => {
      if (cancelled) return
      try {
        const res = await fetchComposition(creativeId)
        if (cancelled) return
        // Only accept a composition whose SHAPE matches the live content — same guard the
        // shape-flip watcher uses. A rebuild's content:updated can briefly precede the new
        // composition write, so a fetch can return the STALE-shape doc; accepting it would mount a
        // doc the render guard rejects (back to read-only) and, since the media signature already
        // advanced, we'd never retry. Keep polling until the matching-shape doc lands instead.
        if (
          res.composition &&
          isCarouselComposition(res.composition) === contentIsCarousel &&
          isVideoComposition(res.composition) === contentIsVideo
        ) {
          setComposition(res.composition)
          setReady(true)
          setStatus(creativeId, 'canvas')
          return
        }
      } catch (err) {
        console.warn('composition media-change refetch failed:', err)
      }
      attempts += 1
      if (!cancelled && attempts < 10) timer = setTimeout(poll, 1200)
    }
    void poll()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [
    mediaSignature,
    composition,
    contentIsCarousel,
    contentIsVideo,
    enabled,
    creativeId,
    setStatus,
  ])

  if (!enabled) return <>{children}</>

  // No composition (older post, video, or fetch failed) → render children with a null
  // context so previews keep their existing static media.
  if (!ready || !composition) return <>{children}</>

  // Never mount an editor whose SHAPE disagrees with the live post content. A carousel creative
  // can carry a stale/orphaned STATIC composition (e.g. a legacy or in-house-built deck that never
  // got a SocialKit deck composition, or a post that used to be a single image) — mounting the
  // single-image editor on it shows one slide's layers + a score unrelated to the actual deck (the
  // "feels wrong" bug). While the shapes disagree we render children with a NULL context → the
  // read-only carousel/image preview shows instead; the shape-change effect above keeps re-fetching,
  // so if a matching-shape composition ever lands it swaps in and the correct editor mounts.
  if (isCarouselComposition(composition) !== contentIsCarousel) return <>{children}</>
  // Same guard for video: never mount the timeline editor on a non-video post, nor a static/carousel
  // editor on a video post. A video composition only mounts on video content; anything else renders
  // children with a null context (read-only preview) until a matching-shape doc lands.
  if (isVideoComposition(composition) !== contentIsVideo) return <>{children}</>
  // Defensive: a video composition with no beats can't seed the active-beat overlay editor
  // (beats[0]). The engine always emits >=MIN_BEATS, but never crash the composer on a bad doc.
  if (isVideoComposition(composition) && composition.beats.length === 0) return <>{children}</>

  // Keyed so a fresh editor mounts per creative + per loaded document. A carousel routes to the
  // deck-aware inner (per-slide editor + slide management); a video to the timeline-aware inner
  // (per-beat overlay editor + beat management + explicit render); a static post to the original.
  if (isVideoComposition(composition)) {
    return (
      <VideoProviderInner key={creativeId} creativeId={creativeId} video={composition}>
        {children}
      </VideoProviderInner>
    )
  }
  if (isCarouselComposition(composition)) {
    return (
      <CarouselProviderInner key={creativeId} creativeId={creativeId} deck={composition}>
        {children}
      </CarouselProviderInner>
    )
  }
  return (
    <ProviderInner key={creativeId} creativeId={creativeId} composition={composition}>
      {children}
    </ProviderInner>
  )
}

function ProviderInner({
  creativeId,
  composition,
  children,
}: {
  creativeId: string
  composition: PostComposition
  children: ReactNode
}) {
  const editor = useCompositionEditor(composition)
  // The CURRENT doc, refreshed every render — so on-exit master-render + the watcher use what's
  // actually on screen now (not a stale snapshot captured at the last manual edit). After an AI
  // generate/revise this is the fresh doc (dirty:false), so exit-render skips it (no clobber).
  const latestCompRef = useRef(editor.comp)
  latestCompRef.current = editor.comp
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [dockOpen, setDockOpen] = useState(false)
  const [previewSeq, setPreviewSeq] = useState<number | null>(null)
  const { replaceComposition } = editor

  // Version preview state. While previewing an older version we suppress autosave AND the
  // external-refresh watcher (so a look-back is never persisted and never clobbers the tip).
  // `tipDocRef` stashes the live tip so exitPreview can restore the canvas to it.
  const previewingRef = useRef<number | null>(null)
  const tipDocRef = useRef<PostComposition | null>(null)
  // The creative's master attachment at the moment preview began. If it changes during
  // preview (an AI revise lands while the master-watcher is suppressed), exitPreview must
  // refetch the fresh tip instead of restoring the now-stale captured snapshot.
  const tipMasterRef = useRef<string | null>(null)

  // Edit/save bookkeeping. `editSeq` advances on every edit; `savedSeq` advances only when a
  // PUT confirms. They diverge exactly while there are local edits the server hasn't accepted
  // (debounce pending, PUT in flight, or a FAILED save). Two things depend on this signal:
  //   1. A failed save must NOT be dropped silently — we retry the freshest doc + surface it.
  //   2. An external refresh (post-revise / post-build watcher below) must NOT replaceComposition
  //      while edits are unsaved, or it overwrites in-memory work with the stale server doc.
  //      This is the data-loss path: a transient 5xx (every prod deploy has a window) leaves
  //      edits unsaved, then any content:updated refetch clobbers them.
  const editSeq = useRef(0)
  const savedSeq = useRef(0)
  const lastComp = useRef<PostComposition | null>(null)
  // True once the user makes a MANUAL edit this session. Gates the on-exit master re-render so a
  // pure view (or an AI generate/revise the user didn't hand-edit) never triggers a render —
  // `meta.dirty` alone can't, it stays true for any ever-edited post until publish.
  const editedThisSession = useRef(false)
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlightSave = useRef<Promise<void> | null>(null)
  const hasUnsavedEdits = useCallback(() => editSeq.current !== savedSeq.current, [])

  // One actual PUT. On success advance savedSeq (so the guard re-arms + the indicator clears);
  // on failure keep the work unsaved, surface it, and retry the latest doc once.
  const doSave = useCallback(
    (c: PostComposition, seq: number): Promise<void> => {
      setSaving(true)
      const p = saveComposition(creativeId, c)
        .then(() => {
          savedSeq.current = Math.max(savedSeq.current, seq)
          setSaveFailed(false)
          // A pending retry is now moot — cancel it so it can't fire a stale PUT later.
          if (retryTimer.current) {
            clearTimeout(retryTimer.current)
            retryTimer.current = null
          }
        })
        .catch(err => {
          console.error('composition autosave failed:', err)
          setSaveFailed(true)
          if (retryTimer.current) clearTimeout(retryTimer.current)
          retryTimer.current = setTimeout(() => {
            if (hasUnsavedEdits() && lastComp.current)
              void doSave(lastComp.current, editSeq.current)
          }, SAVE_RETRY_MS)
        })
        .finally(() => setSaving(false))
      inFlightSave.current = p
      return p
    },
    [creativeId, hasUnsavedEdits]
  )

  const save = useMemo(
    () => debounce((c: PostComposition, seq: number) => void doSave(c, seq), AUTOSAVE_MS),
    [doSave]
  )

  // Autosave on every edit (dirty flips true on the first edit and stays true until
  // the server clears it at publish, so each subsequent change debounce-saves).
  useEffect(() => {
    // Never autosave while previewing an older version — a look-back is transient, and
    // persisting it would overwrite the live tip with a restored older doc.
    if (editor.comp.meta.dirty && previewingRef.current == null) {
      editSeq.current += 1
      lastComp.current = editor.comp
      editedThisSession.current = true
      save(editor.comp, editSeq.current)
    }
  }, [editor.comp, save])

  // Flush a pending save on unmount + before the tab unloads, so no edit is lost. AND re-render the
  // flat MASTER from the doc on exit when there are UNRENDERED manual edits — otherwise the master
  // only re-renders at publish, so home/calendar/posts keep showing the stale pre-edit image.
  //
  // CRITICAL: render the CURRENT doc (latestCompRef) gated on its OWN meta.dirty, never a snapshot
  // captured at the last manual edit. After an AI generate/revise the watcher below swaps in the
  // server doc with dirty:false, so this skips — the AI already produced a consistent master, and
  // rendering a stale snapshot here would CLOBBER the good generated composition (the "jumped back
  // to old version" bug). Fire-and-forget: SPA nav keeps the app alive so the render completes +
  // content:updated refreshes other surfaces; a failure is harmless (publish re-renders the dirty doc).
  useEffect(() => {
    const flush = () => save.flush()
    window.addEventListener('beforeunload', flush)
    return () => {
      window.removeEventListener('beforeunload', flush)
      save.flush()
      if (retryTimer.current) clearTimeout(retryTimer.current)
      const doc = latestCompRef.current
      // Fire only when the user MANUALLY edited this session AND the current doc still carries those
      // edits (dirty). After an AI generate/revise the watcher swaps in a dirty:false server doc, so
      // this skips — no stale-snapshot clobber, and no wasteful render on a pure view of a post whose
      // `meta.dirty` is just left over from a past session.
      if (editedThisSession.current && doc?.meta.dirty) {
        // Mark the render as fired so the self-heal (a reopen while the server
        // is still rendering) can't double-fire for this creative.
        masterRenderFiredThisSession.add(creativeId)
        renderComposition(creativeId, doc).catch(err => {
          console.error('composition master re-render on exit failed:', err)
          captureRenderFailure(err, 'exit_render', creativeId)
          // Un-mark so reopening the post self-heals instead of staying stuck
          // behind this session's failed attempt.
          masterRenderFiredThisSession.delete(creativeId)
          // One delayed retry of the STORED doc (the autosave has landed by
          // now). Worker render 500s are typically transient blips — the prod
          // e2e caught an identical payload succeeding seconds after a 500 —
          // and without a retry the "Applying your edits" chip stays stuck
          // until the user happens to reopen the post (self-heal) or publish.
          setTimeout(() => {
            if (masterRenderFiredThisSession.has(creativeId)) return
            masterRenderFiredThisSession.add(creativeId)
            renderComposition(creativeId).catch(retryErr => {
              console.error('composition exit-render retry failed:', retryErr)
              captureRenderFailure(retryErr, 'exit_render_retry', creativeId)
              masterRenderFiredThisSession.delete(creativeId)
            })
          }, 8_000)
        })
      } else if (!editedThisSession.current) {
        // Self-heal a stuck master on the way OUT of a view-only session (see
        // selfHealStuckMasterRender). Deliberately on unmount, not mount: an
        // edit-session's exit render (above) and a mount heal could render
        // concurrently and let the OLDER doc's pixels win the master swap.
        // Leaving un-edited means no exit render exists to race.
        selfHealStuckMasterRender(creativeId)
      }
    }
  }, [save, creativeId])

  // ── Post-revise live refresh ───────────────────────────────────────────────
  // An AI revise/regenerate re-renders the master image and swaps the creative's
  // imageAttachmentId (via the creative:content:updated Pusher event). Our own
  // autosave never touches imageAttachmentId (the master is rendered at publish),
  // so a change here means the AI produced a NEW document server-side — refetch it
  // and reset the editor so the canvas shows the AI's rewrite (with the user's
  // merged layout) live, instead of the stale pre-revise doc.
  const masterAttachmentId = useCreativeStore(
    s => s.creatives.find(c => c.id === creativeId)?.content?.imageAttachmentId ?? null
  )
  // The master the currently-loaded composition corresponds to. Seeded on mount so the
  // initial value never triggers a refetch.
  const knownMasterRef = useRef<string | null | undefined>(undefined)
  // A master change that landed while local edits were unsaved. We must not clobber the
  // in-memory work, but we also must not drop the AI's doc forever (the effect below only
  // re-runs on the NEXT master change, which may never come) — remember it and retry once
  // the save settles.
  const pendingMasterRef = useRef<string | null>(null)
  // Monotonic guard so a slow refetch can't overwrite the result of a newer one.
  const refreshSeqRef = useRef(0)

  const refreshFromMaster = useCallback(
    (master: string) => {
      pendingMasterRef.current = null
      const seq = ++refreshSeqRef.current
      fetchComposition(creativeId)
        .then(res => {
          // This provider only edits a static post; a carousel refetch belongs to the deck provider.
          if (
            seq !== refreshSeqRef.current ||
            !res.composition ||
            !isStaticComposition(res.composition)
          )
            return
          // An edit or a preview raced in while the fetch was in flight — keep the local doc and
          // PARK the master (knownMasterRef deliberately not advanced yet, or the settle-retry's
          // `pending === known` guard could never re-run this refresh).
          if (previewingRef.current != null || hasUnsavedEdits()) {
            pendingMasterRef.current = master
            return
          }
          knownMasterRef.current = master
          const fresh = res.composition
          // Skip when the server doc matches what's on screen (our own edit's master echo) — only
          // a real AI revise/generate produces a DIFFERENT doc worth swapping in. Avoids needlessly
          // resetting undo/selection, and avoids replacing a fresh doc with an equal refetch.
          if (sameDoc(fresh, latestCompRef.current)) return
          // Treat the server doc as clean so the reset doesn't re-trigger autosave.
          replaceComposition({ ...fresh, meta: { ...fresh.meta, dirty: false } })
        })
        .catch(err => {
          // Park so the next save cycle retries instead of leaving the canvas stale forever.
          pendingMasterRef.current = master
          console.warn('post-revise composition refresh failed:', err)
        })
    },
    [creativeId, replaceComposition, hasUnsavedEdits]
  )

  useEffect(() => {
    if (knownMasterRef.current === undefined) {
      knownMasterRef.current = masterAttachmentId
      return
    }
    if (!masterAttachmentId || masterAttachmentId === knownMasterRef.current) return
    // While previewing an older version, ignore master changes — the canvas is intentionally
    // showing a look-back and must not be swapped out from under it (exitPreview refetches).
    if (previewingRef.current != null) return
    // Never overwrite unsaved local edits. A legit AI revise flushes the editor first
    // (SideChat.flushSave), but the user may have kept editing while the AI worked, or the
    // flush PUT may have failed — park the master and refresh once the save settles.
    if (hasUnsavedEdits()) {
      pendingMasterRef.current = masterAttachmentId
      return
    }
    refreshFromMaster(masterAttachmentId)
  }, [masterAttachmentId, hasUnsavedEdits, refreshFromMaster])

  // Settle-retry: a refresh skipped mid-edit (or mid-preview) re-arms here once the autosave
  // round-trip finishes or the preview exits. If the user's save landed AFTER the AI's doc,
  // the refetch comes back equal to the local doc and the swap is a no-op — no work is ever
  // lost, but the canvas can never stay permanently stale either.
  useEffect(() => {
    if (saving) return
    const pending = pendingMasterRef.current
    if (!pending || pending === knownMasterRef.current) return
    if (previewingRef.current != null || hasUnsavedEdits()) return
    refreshFromMaster(pending)
  }, [saving, previewSeq, hasUnsavedEdits, refreshFromMaster])

  const value = useMemo<CompositionEditorContextValue>(
    () => ({
      editor,
      creativeId,
      saving,
      saveFailed,
      dock: { open: dockOpen, setOpen: setDockOpen, openDock: () => setDockOpen(true) },
      flushSave: async () => {
        // Cancel any pending failure-retry so it can't fire a stale PUT after this flush.
        if (retryTimer.current) {
          clearTimeout(retryTimer.current)
          retryTimer.current = null
        }
        save.flush()
        if (inFlightSave.current) await inFlightSave.current
        // A prior save may have failed (savedSeq still behind). Make one more synchronous
        // attempt so an AI revise merges the user's latest edits, not a stale snapshot.
        if (hasUnsavedEdits() && lastComp.current) await doSave(lastComp.current, editSeq.current)
      },
      previewSeq,
      previewVersion: async (seq: number) => {
        if (previewingRef.current === seq) return
        // Entering preview from the live tip: persist any pending edit FIRST (so the tip we
        // capture + restore matches the server), then snapshot the tip doc + its master.
        if (previewingRef.current == null) {
          save.flush()
          if (inFlightSave.current) await inFlightSave.current
          tipDocRef.current = latestCompRef.current
          tipMasterRef.current =
            useCreativeStore.getState().creatives.find(c => c.id === creativeId)?.content
              ?.imageAttachmentId ?? null
        }
        const detail = await useVersionsStore.getState().fetchDetail(creativeId, seq)
        if (!detail?.composition || !isStaticComposition(detail.composition)) return
        const doc = detail.composition
        previewingRef.current = seq
        setPreviewSeq(seq)
        replaceComposition({ ...doc, meta: { ...doc.meta, dirty: false } })
      },
      exitPreview: () => {
        if (previewingRef.current == null) return
        const tip = tipDocRef.current
        const tipMaster = tipMasterRef.current
        previewingRef.current = null
        setPreviewSeq(null)
        tipDocRef.current = null
        tipMasterRef.current = null
        const nowMaster =
          useCreativeStore.getState().creatives.find(c => c.id === creativeId)?.content
            ?.imageAttachmentId ?? null
        if (nowMaster && nowMaster !== tipMaster) {
          // The tip changed while we previewed (an AI revise landed with the master-watcher
          // suppressed) — refetch the fresh tip rather than restore the stale snapshot, and
          // resync knownMasterRef so the watcher doesn't then double-refresh.
          knownMasterRef.current = nowMaster
          fetchComposition(creativeId)
            .then(res => {
              if (res?.composition && isStaticComposition(res.composition)) {
                const fresh = res.composition
                replaceComposition({ ...fresh, meta: { ...fresh.meta, dirty: false } })
              }
            })
            .catch(err => console.warn('exit-preview tip refresh failed:', err))
          return
        }
        if (tip) replaceComposition({ ...tip, meta: { ...tip.meta, dirty: false } })
      },
      restoreVersion: async (seq: number) => {
        // Leave preview WITHOUT locally restoring the tip — the server restore drives the
        // new tip, which we then load deterministically below.
        previewingRef.current = null
        setPreviewSeq(null)
        tipDocRef.current = null
        await useVersionsStore.getState().restore(creativeId, seq)
        // Refresh the score panel to the restored score + swap the canvas to the restored tip.
        await useCreativeProcessStore.getState().fetchGeneration(creativeId)
        const res = await fetchComposition(creativeId).catch(() => null)
        if (res?.composition && isStaticComposition(res.composition)) {
          const fresh = res.composition
          replaceComposition({ ...fresh, meta: { ...fresh.meta, dirty: false } })
        }
      },
    }),
    [
      editor,
      creativeId,
      saving,
      saveFailed,
      dockOpen,
      previewSeq,
      save,
      doSave,
      hasUnsavedEdits,
      replaceComposition,
    ]
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

// A signature of the deck's rendered masters, so the post-revise watcher can tell when an AI
// carousel revise swapped the mediaItems (the deck's "master") and refetch the fresh deck.
function mediaItemsSig(items: unknown): string {
  if (!Array.isArray(items)) return ''
  return items
    .map(m =>
      m && typeof m === 'object' ? String((m as { attachmentId?: unknown }).attachmentId ?? '') : ''
    )
    .join('|')
}

// Deck twin of sameDoc: same slide count, same deck-level caption/hashtags, every slide visually
// equal. Lets the post-revise watcher skip a reset when the refetched deck is what's on screen.
function sameDeck(
  a: CarouselComposition | null | undefined,
  b: CarouselComposition | null | undefined
): boolean {
  if (!a || !b || a.slides.length !== b.slides.length) return false
  if (a.caption !== b.caption || JSON.stringify(a.hashtags) !== JSON.stringify(b.hashtags))
    return false
  return a.slides.every((slide, i) => sameDoc(slide, b.slides[i]))
}

// Deck-level undo depth (structural ops are coarse; 50 matches the slide editor's cap).
const DECK_MAX_HISTORY = 50

// The carousel editor: holds the whole deck + an active slide index, and feeds the ACTIVE slide
// into the same single-doc useCompositionEditor so every per-slide tool (layers/text/image/undo)
// works unchanged. An active-slide edit is written back into the deck and the WHOLE deck autosaves;
// slide management (add/delete/duplicate/reorder) mutates the deck directly.
function CarouselProviderInner({
  creativeId,
  deck: initialDeck,
  children,
}: {
  creativeId: string
  deck: CarouselComposition
  children: ReactNode
}) {
  const editor = useCompositionEditor(initialDeck.slides[0]!)
  const [deck, setDeck] = useState<CarouselComposition>(initialDeck)
  const [activeIndex, setActiveIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [dockOpen, setDockOpen] = useState(false)
  const { replaceComposition } = editor

  const deckRef = useRef(deck)
  deckRef.current = deck
  const activeRef = useRef(activeIndex)
  activeRef.current = activeIndex
  const previewingRef = useRef<number | null>(null)
  const [previewSeq, setPreviewSeq] = useState<number | null>(null)
  const inFlightSave = useRef<Promise<void> | null>(null)

  // F-14: structural deck edits (reorder/add/delete/duplicate) get their own undo
  // history — they used to be irreversible while text edits were undoable. Routing
  // with the per-slide editor is unambiguous because every deck op goes through
  // replaceComposition, which CLEARS the slide editor's stacks: undo drains slide
  // edits made since the last deck op first, then pops deck snapshots (chronological).
  // The tick state re-derives canUndo/canRedo when only the ref-held stacks change.
  const deckPast = useRef<Array<{ deck: CarouselComposition; active: number }>>([])
  const deckFuture = useRef<Array<{ deck: CarouselComposition; active: number }>>([])
  const [, setDeckHistoryTick] = useState(0)
  const bumpDeckHistory = useCallback(() => setDeckHistoryTick(n => n + 1), [])
  // Preview/restore/AI-refresh swap the WHOLE deck outside the op path — undoing
  // across those would resurrect a stale deck, so they reset the history instead.
  const resetDeckHistory = useCallback(() => {
    deckPast.current = []
    deckFuture.current = []
    setDeckHistoryTick(n => n + 1)
  }, [])

  const doSave = useCallback(
    (d: CarouselComposition): Promise<void> => {
      setSaving(true)
      const p = saveComposition(creativeId, d)
        .then(() => setSaveFailed(false))
        .catch(err => {
          console.error('carousel composition autosave failed:', err)
          setSaveFailed(true)
        })
        .finally(() => setSaving(false))
      inFlightSave.current = p
      return p
    },
    [creativeId]
  )
  const save = useMemo(
    () => debounce((d: CarouselComposition) => void doSave(d), AUTOSAVE_MS),
    [doSave]
  )

  // Self-heal a stuck master render on mount (see selfHealStuckMasterRender). The
  // deck has no exit render (the server renders it on publish / after AI turns),
  // so a stuck flag here means a past static session or server render never
  // landed — the shared endpoint branches on the stored doc's shape either way.
  useEffect(() => {
    selfHealStuckMasterRender(creativeId)
  }, [creativeId])

  // Persist a mutated deck: keep our ref fresh, update state, and autosave (unless previewing).
  const commitDeck = useCallback(
    (next: CarouselComposition) => {
      deckRef.current = next
      setDeck(next)
      if (previewingRef.current == null) save(next)
    },
    [save]
  )

  // Write an active-slide edit back into the deck + autosave the whole deck. Gated on the slide's
  // own dirty flag, so a replaceComposition (slide switch / AI refresh, which lands dirty:false)
  // never triggers a write-back loop.
  useEffect(() => {
    if (!editor.comp.meta.dirty) return
    const i = activeRef.current
    const cur = deckRef.current
    if (cur.slides[i] === editor.comp) return
    // A fresh edit invalidates the deck REDO branch (standard editor convention):
    // after a deck undo, redoing over new work would time-travel it away.
    if (deckFuture.current.length) {
      deckFuture.current = []
      bumpDeckHistory()
    }
    const slides = cur.slides.slice()
    slides[i] = editor.comp
    commitDeck({ ...cur, slides, meta: { ...cur.meta, dirty: true } })
  }, [editor.comp, commitDeck, bumpDeckHistory])

  const selectSlide = useCallback(
    (i: number) => {
      if (i === activeRef.current || i < 0 || i >= deckRef.current.slides.length) return
      setActiveIndex(i)
      replaceComposition({
        ...deckRef.current.slides[i]!,
        meta: { ...deckRef.current.slides[i]!.meta, dirty: false },
      })
    },
    [replaceComposition]
  )

  // Apply a deck-level slide op, then re-sync the active slide + editor to `nextActive`.
  // Pushes the PRE-op deck onto the deck history first, so the op is undoable (F-14).
  const applyDeckOp = useCallback(
    (next: CarouselComposition, nextActive: number) => {
      if (previewingRef.current == null) {
        deckPast.current.push({ deck: deckRef.current, active: activeRef.current })
        if (deckPast.current.length > DECK_MAX_HISTORY) deckPast.current.shift()
        deckFuture.current = []
        bumpDeckHistory()
      }
      const clamped = Math.max(0, Math.min(nextActive, next.slides.length - 1))
      commitDeck(next)
      setActiveIndex(clamped)
      replaceComposition({
        ...next.slides[clamped]!,
        meta: { ...next.slides[clamped]!.meta, dirty: false },
      })
    },
    [commitDeck, replaceComposition, bumpDeckHistory]
  )

  // Restore a deck snapshot (undo/redo of a structural op). Stamped dirty so the
  // autosave persists the restored deck — a clean-flagged restore would leave the
  // SERVER on the undone state and any refetch would resurrect the undone op
  // (mirrors the same rule in useCompositionEditor.undo). The save also re-judges,
  // so the score tracks the restored deck honestly.
  const restoreDeckSnapshot = useCallback(
    (entry: { deck: CarouselComposition; active: number }) => {
      const restored = { ...entry.deck, meta: { ...entry.deck.meta, dirty: true } }
      const clamped = Math.max(0, Math.min(entry.active, restored.slides.length - 1))
      commitDeck(restored)
      setActiveIndex(clamped)
      replaceComposition({
        ...restored.slides[clamped]!,
        meta: { ...restored.slides[clamped]!.meta, dirty: false },
      })
    },
    [commitDeck, replaceComposition]
  )

  const deckUndo = useCallback(() => {
    const entry = deckPast.current.pop()
    if (!entry) return
    deckFuture.current.push({ deck: deckRef.current, active: activeRef.current })
    bumpDeckHistory()
    restoreDeckSnapshot(entry)
  }, [bumpDeckHistory, restoreDeckSnapshot])

  const deckRedo = useCallback(() => {
    const entry = deckFuture.current.pop()
    if (!entry) return
    deckPast.current.push({ deck: deckRef.current, active: activeRef.current })
    bumpDeckHistory()
    restoreDeckSnapshot(entry)
  }, [bumpDeckHistory, restoreDeckSnapshot])

  const deckValue = useMemo<CarouselDeckContextValue>(
    () => ({
      slides: deck.slides,
      activeIndex,
      selectSlide,
      addSlide: () => {
        const next = opAddSlide(deckRef.current)
        if (next !== deckRef.current) applyDeckOp(next, next.slides.length - 1)
      },
      duplicateSlide: (i: number) => {
        const next = opDuplicateSlide(deckRef.current, i)
        // Follow the slide you were editing: a copy inserted at i+1 shifts anything
        // at/after i+1 down by one. Duplicating the ACTIVE slide selects the new copy.
        if (next !== deckRef.current) {
          const active = activeRef.current
          applyDeckOp(next, i === active ? i + 1 : active > i ? active + 1 : active)
        }
      },
      deleteSlide: (i: number) => {
        const next = opDeleteSlide(deckRef.current, i)
        // Keep focus on the slide being edited: deleting a slide BEFORE it shifts it
        // down by one; deleting the active slide (or one after) clamps to i-1/0.
        if (next !== deckRef.current) {
          const active = activeRef.current
          applyDeckOp(next, active > i ? active - 1 : active < i ? active : i > 0 ? i - 1 : 0)
        }
      },
      moveSlide: (from: number, to: number) => {
        const next = opMoveSlide(deckRef.current, from, to)
        // Remap the active index through the move so reordering a NON-active slide
        // doesn't steal focus; moving the active slide follows it to `to`.
        if (next !== deckRef.current) {
          const active = activeRef.current
          const remapped =
            active === from
              ? to
              : from < active && active <= to
                ? active - 1
                : to <= active && active < from
                  ? active + 1
                  : active
          applyDeckOp(next, remapped)
        }
      },
      canAdd: deck.slides.length < MAX_SLIDES,
      canDelete: deck.slides.length > MIN_SLIDES,
    }),
    [deck.slides, activeIndex, selectSlide, applyDeckOp]
  )

  // Flush pending save on unmount + before unload (no edit lost).
  useEffect(() => {
    const flush = () => save.flush()
    window.addEventListener('beforeunload', flush)
    return () => {
      window.removeEventListener('beforeunload', flush)
      save.flush()
    }
  }, [save])

  // Post-revise live refresh: an AI carousel revise swaps the deck's mediaItems (its "master"),
  // or rewrites only the deck-level caption/hashtags and moves no slide at all. Either way the
  // server holds a NEW deck document, so (unless previewing; a real revise flushes edits first)
  // refetch it and reset the editor so the canvas shows the AI's rewrite (with merged edits).
  // Keyed on the mediaItems signature AND the store's server-content version: the signature
  // catches slide swaps, the version a caption-only revise. User typing never bumps the
  // version (creative-store.updateCreativeContent), so the caption box cannot trigger this.
  const mediaSig = useCreativeStore(s => {
    // mediaItems only exists on gallery content types (IG/FB), not e.g. TikTokVideoContent.
    const content = s.creatives.find(c => c.id === creativeId)?.content
    return mediaItemsSig(content && 'mediaItems' in content ? content.mediaItems : undefined)
  })
  const serverContentVersion = useCreativeStore(s => s.serverContentVersions[creativeId] ?? 0)
  const knownSig = useRef<string | null>(null)
  const knownVersion = useRef<number>(0)
  useEffect(() => {
    if (knownSig.current === null) {
      knownSig.current = mediaSig
      knownVersion.current = serverContentVersion
      return
    }
    const sigChanged = !!mediaSig && mediaSig !== knownSig.current
    const versionChanged = serverContentVersion !== knownVersion.current
    if (!sigChanged && !versionChanged) return
    if (previewingRef.current != null) return
    knownSig.current = mediaSig
    knownVersion.current = serverContentVersion
    let cancelled = false
    fetchComposition(creativeId)
      .then(res => {
        if (cancelled || !res.composition || !isCarouselComposition(res.composition)) return
        const fresh = res.composition
        // Skip when the server deck matches what's on screen (a render-completion flag, or the
        // Pusher echo of a deck we already refetched): only a real rewrite is worth resetting
        // undo/selection for.
        if (sameDeck(fresh, deckRef.current)) return
        resetDeckHistory()
        deckRef.current = fresh
        setDeck(fresh)
        const i = Math.max(0, Math.min(activeRef.current, fresh.slides.length - 1))
        setActiveIndex(i)
        replaceComposition({
          ...fresh.slides[i]!,
          meta: { ...fresh.slides[i]!.meta, dirty: false },
        })
      })
      .catch(err => console.warn('post-revise carousel refresh failed:', err))
    return () => {
      cancelled = true
    }
  }, [mediaSig, serverContentVersion, creativeId, replaceComposition, resetDeckHistory])

  const value = useMemo<CompositionEditorContextValue>(
    () => ({
      // The shared undo affordance sees ONE history: slide-level edits first (they
      // are cleared by every deck op, so they're always the most recent), then the
      // deck snapshots behind them (F-14 — structural ops used to be irreversible).
      editor: {
        ...editor,
        canUndo: editor.canUndo || deckPast.current.length > 0,
        canRedo: editor.canRedo || deckFuture.current.length > 0,
        undo: () => (editor.canUndo ? editor.undo() : deckUndo()),
        redo: () => (editor.canRedo ? editor.redo() : deckRedo()),
      },
      creativeId,
      saving,
      saveFailed,
      dock: { open: dockOpen, setOpen: setDockOpen, openDock: () => setDockOpen(true) },
      flushSave: async () => {
        save.flush()
        if (inFlightSave.current) await inFlightSave.current
      },
      previewSeq,
      previewVersion: async (seq: number) => {
        if (previewingRef.current === seq) return
        if (previewingRef.current == null) {
          save.flush()
          if (inFlightSave.current) await inFlightSave.current
        }
        const detail = await useVersionsStore.getState().fetchDetail(creativeId, seq)
        if (!detail?.composition || !isCarouselComposition(detail.composition)) return
        resetDeckHistory()
        previewingRef.current = seq
        setPreviewSeq(seq)
        const d = detail.composition
        deckRef.current = d
        setDeck(d)
        setActiveIndex(0)
        replaceComposition({ ...d.slides[0]!, meta: { ...d.slides[0]!.meta, dirty: false } })
      },
      exitPreview: () => {
        if (previewingRef.current == null) return
        previewingRef.current = null
        setPreviewSeq(null)
        resetDeckHistory()
        fetchComposition(creativeId)
          .then(res => {
            if (!res?.composition || !isCarouselComposition(res.composition)) return
            const d = res.composition
            deckRef.current = d
            setDeck(d)
            setActiveIndex(0)
            replaceComposition({ ...d.slides[0]!, meta: { ...d.slides[0]!.meta, dirty: false } })
          })
          .catch(err => console.warn('exit-preview carousel refresh failed:', err))
      },
      restoreVersion: async (seq: number) => {
        previewingRef.current = null
        setPreviewSeq(null)
        resetDeckHistory()
        await useVersionsStore.getState().restore(creativeId, seq)
        await useCreativeProcessStore.getState().fetchGeneration(creativeId)
        const res = await fetchComposition(creativeId).catch(() => null)
        if (res?.composition && isCarouselComposition(res.composition)) {
          const d = res.composition
          deckRef.current = d
          setDeck(d)
          setActiveIndex(0)
          replaceComposition({ ...d.slides[0]!, meta: { ...d.slides[0]!.meta, dirty: false } })
        }
      },
    }),
    [
      editor,
      creativeId,
      saving,
      saveFailed,
      dockOpen,
      previewSeq,
      save,
      replaceComposition,
      deckUndo,
      deckRedo,
      resetDeckHistory,
    ]
  )

  return (
    <Ctx.Provider value={value}>
      <DeckCtx.Provider value={deckValue}>{children}</DeckCtx.Provider>
    </Ctx.Provider>
  )
}

// The video editor: holds the whole TIMELINE (VideoComposition) + an active beat index, and feeds
// the ACTIVE beat's `overlay` (a PostComposition on a transparent canvas) into the same single-doc
// useCompositionEditor — so every per-beat text tool (layers/text/undo) works unchanged, exactly
// like a carousel slide. An active-beat overlay edit is written back into beats[i].overlay and the
// WHOLE video autosaves. Beat management (add/delete/duplicate/reorder/trim/VO/music) mutates the
// timeline via the pure video-ops.
//
// CRITICAL DIFFERENCE from static/carousel: autosave NEVER renders the master. The reel is
// expensive to re-composite (VO synth + clip stitch), so the master render is driven ONLY by the
// explicit render() below — there is no on-exit / on-dirty master render here.
function VideoProviderInner({
  creativeId,
  video: initialVideo,
  children,
}: {
  creativeId: string
  video: VideoComposition
  children: ReactNode
}) {
  // N1: every document is lifted to the deck-level clip representation on entry; the editor
  // holds the ACTIVE-BEAT VIEW (clips intersecting the beat's span), reconciled back by id.
  const [video, setVideo] = useState<VideoComposition>(() =>
    normalizeVideoComposition(initialVideo)
  )
  const [initialView] = useState<PostComposition>(() => videoEditorViewDoc(video, 0))
  const editor = useCompositionEditor(initialView)
  // The layer ids the CURRENT editor view was synthesized with. The write-back reconcile
  // diffs against THIS set (not the live span membership): a clip dragged into/out of the
  // active span between view syncs must not read as "added"/"deleted in the editor".
  const viewIdsRef = useRef<Set<string>>(new Set(initialView.layers.map(l => l.id)))
  const [activeIndex, setActiveIndex] = useState(0)
  const [saving, setSaving] = useState(false)
  const [saveFailed, setSaveFailed] = useState(false)
  const [dockOpen, setDockOpen] = useState(false)
  const [rendering, setRendering] = useState(false)
  const renderingRef = useRef(false)
  renderingRef.current = rendering
  // The in-flight render reached the server (videoStatus flipped to 'generating'). A later
  // videoStatus revert WITHOUT a new master URL means the background render FAILED server-side
  // — the only completion signal the videoSig watcher would otherwise wait 12 minutes for.
  const sawRenderGenerating = useRef(false)
  const [renderError, setRenderError] = useState<string | null>(null)
  // Live render-progress label: the server relays the worker's coarse stage as a
  // creative:content:updated field (videoRenderPhase) while the composite runs off-request.
  const [renderPhase, setRenderPhase] = useState<string | null>(null)
  // Blueprint hydration: the server films planned beats off-request and relays progress as a
  // videoHydration content field. On each tick, pull the latest composition and slot freshly
  // filmed clips into their beats BY ID — never touching a beat the user edited meanwhile, and
  // WITHOUT commitVideo (the server already holds this state; an autosave would re-mark dirty).
  const [hydration, setHydration] = useState<{ done: number; total: number } | null>(null)
  // Late-bound: re-arm a PENDING debounced autosave with a server-merged doc (voice/hydration
  // applies), so its stale pre-merge snapshot can't clobber the server's write. Bound after the
  // save machinery below (declared here because the hydration/voice callbacks close over it).
  const rearmPendingSave = useRef<(doc: VideoComposition) => void>(() => {})
  // Whole-reel undo stacks (declared up here so the hydration/voice adopters below can REBASE
  // them: an old snapshot restored by Cmd+Z after a beat filmed would otherwise PUT the beat
  // back to `planned`/voiceless and un-film it server-side).
  const videoPast = useRef<VideoComposition[]>([])
  const videoFuture = useRef<VideoComposition[]>([])
  const historyGestureSnap = useRef<VideoComposition | null>(null)
  const historyGestureActive = useRef(false)
  const rebaseHistory = useCallback((adopt: (doc: VideoComposition) => VideoComposition) => {
    videoPast.current = videoPast.current.map(adopt)
    videoFuture.current = videoFuture.current.map(adopt)
    if (historyGestureSnap.current) historyGestureSnap.current = adopt(historyGestureSnap.current)
  }, [])
  // Set on unmount: long-running polls (beat-clip regenerate) must stop writing after the
  // provider is gone.
  const unmountedRef = useRef(false)
  useEffect(() => {
    // Reset on setup, not just flag on cleanup: StrictMode's dev double-invoke runs
    // setup → cleanup → setup on the SAME instance, so a cleanup-only flag would stay
    // true forever and every long poll (beat clips, music) would exit on its first tick.
    unmountedRef.current = false
    return () => {
      unmountedRef.current = true
    }
  }, [])

  const applyHydratedBeats = useCallback(async () => {
    const res = await fetchComposition(creativeId).catch(() => null)
    const fresh = res?.composition
    if (!fresh || !isVideoComposition(fresh)) return
    const adopt = (doc: VideoComposition): VideoComposition => {
      let changed = false
      const beats = doc.beats.map(b => {
        const freshBeat = fresh.beats.find(f => f.id === b.id)
        if (!freshBeat) return b
        let next = b
        // Freshly filmed clip → slot it in by id (never touching a beat the user edited).
        const bg = b.background
        const stillPlanned = bg.kind === 'video' && bg.planned === true && !bg.clipSrc
        if (
          stillPlanned &&
          !b.userEdited &&
          freshBeat.background.kind === 'video' &&
          freshBeat.background.clipSrc
        ) {
          changed = true
          next = { ...next, background: freshBeat.background }
        }
        // Freshly recorded VOICE (F4: hydration synths VO before the clip waves) → adopt the
        // take + word-timed captions when we have none and the script hasn't changed locally,
        // so waveforms appear on the timeline WHILE the scenes film.
        if (!next.voSrc && !next.voDirty && freshBeat.voSrc && freshBeat.vo === next.vo) {
          changed = true
          next = { ...next, voSrc: freshBeat.voSrc, captions: freshBeat.captions, voDirty: false }
        }
        return next
      })
      return changed ? { ...doc, beats } : doc
    }
    // Rebase undo history with the same adoption: a pre-hydration snapshot restored later
    // must not resurrect a planned/voiceless beat (undo stamps dirty + autosaves).
    rebaseHistory(adopt)
    const cur = videoRef.current
    const next = adopt(cur)
    if (next !== cur) {
      videoRef.current = next
      setVideo(next)
      // Re-arm a pending autosave with the merged doc — its pre-hydration snapshot would
      // otherwise PUT the beat back to `planned` and the hydrator would re-film it.
      rearmPendingSave.current(next)
    }
  }, [creativeId, rebaseHistory])
  useListenToPusherEvent<{ creativeId: string; fields: Record<string, unknown> }>(
    PusherEvent.CreativeContentUpdated,
    d => {
      if (d.creativeId !== creativeId) return
      if ('videoRenderPhase' in d.fields) {
        setRenderPhase((d.fields.videoRenderPhase as string | null) ?? null)
      }
      if ('videoHydration' in d.fields) {
        setHydration((d.fields.videoHydration as { done: number; total: number } | null) ?? null)
        void applyHydratedBeats()
      }
      // Failed-render detection: the server marks videoStatus='generating' up front and, on a
      // background failure, REVERTS it (null, or 'ready' when a prior master exists) without a
      // new videoUrl/videoAttachmentId. A successful render always lands the new master in the
      // SAME update, so a status settle without one is the failure signal — surface it instead
      // of leaving the dock on "Rendering…" until the 12-minute safety timeout.
      if ('videoStatus' in d.fields) {
        const vs = d.fields.videoStatus as string | null
        if (vs === 'generating') {
          sawRenderGenerating.current = true
        } else if (
          renderingRef.current &&
          sawRenderGenerating.current &&
          !('videoUrl' in d.fields) &&
          !('videoAttachmentId' in d.fields)
        ) {
          sawRenderGenerating.current = false
          if (renderTimer.current) clearTimeout(renderTimer.current)
          setRendering(false)
          setRenderPhase(null)
          setRenderError('The render failed before finishing. Your edits are safe, try again.')
        }
      }
    }
  )
  // Seed hydration from the creative's CURRENT content: the composer usually mounts while a
  // hydration is already in flight (the draft opened from Home mid-filming), and the next
  // Pusher tick can be minutes away on a busy clip queue — event-only state would show the
  // parked "Bring it to life" CTA over an actively-filming reel. The store also updates on
  // content events, so this doubles as a catch-up for any missed tick.
  const storeHydration = useCreativeStore(s => {
    const content = s.creatives.find(c => c.id === creativeId)?.content as unknown as
      Record<string, unknown> | undefined
    const h = content?.videoHydration as { done: number; total: number } | undefined
    return h ? `${h.done}/${h.total}` : null
  })
  useEffect(() => {
    if (!storeHydration) return
    const [done, total] = storeHydration.split('/').map(Number)
    setHydration({ done, total })
    void applyHydratedBeats()
  }, [storeHydration, applyHydratedBeats])
  const bringToLife = useCallback(async () => {
    // Optimistic: show progress immediately; the first server tick corrects the numbers.
    const total = videoRef.current.beats.length
    const planned = videoRef.current.beats.filter(
      b => b.background.kind === 'video' && b.background.planned === true && !b.background.clipSrc
    ).length
    setHydration({ done: total - planned, total })
    try {
      await bringToLifeApi(creativeId)
    } catch (err) {
      console.error('bring-to-life failed:', err)
      setHydration(null)
    }
  }, [creativeId])
  // K6: the persisted score reflects the LAST rendered reel. Any edit since then makes it stale
  // (a video never auto-re-scores — only an explicit Render re-judges). Drives the "score stale"
  // hint next to Render. Set on every edit; cleared when a fresh render lands (videoSig watcher).
  const [editedSinceRender, setEditedSinceRender] = useState(false)
  const { replaceComposition } = editor

  // Swap the editor to a fresh view doc AND record its membership (see viewIdsRef).
  const syncView = useCallback(
    (doc: PostComposition) => {
      viewIdsRef.current = new Set(doc.layers.map(l => l.id))
      replaceComposition(doc)
    },
    [replaceComposition]
  )

  const videoRef = useRef(video)
  videoRef.current = video
  const activeRef = useRef(activeIndex)
  activeRef.current = activeIndex
  const previewingRef = useRef<number | null>(null)
  const [previewSeq, setPreviewSeq] = useState<number | null>(null)
  const inFlightSave = useRef<Promise<void> | null>(null)
  // Diverges from savedSeq exactly while local edits are unsaved — the same gate the static/carousel
  // providers use to stop an external (post-revise) refetch from clobbering in-memory work.
  const editSeq = useRef(0)
  const savedSeq = useRef(0)
  const hasUnsavedEdits = useCallback(() => editSeq.current !== savedSeq.current, [])

  const doSave = useCallback(
    (v: VideoComposition, seq: number): Promise<void> => {
      setSaving(true)
      const p = saveComposition(creativeId, v)
        .then(() => {
          savedSeq.current = Math.max(savedSeq.current, seq)
          setSaveFailed(false)
        })
        .catch(err => {
          console.error('video composition autosave failed:', err)
          setSaveFailed(true)
        })
        .finally(() => setSaving(false))
      inFlightSave.current = p
      return p
    },
    [creativeId]
  )
  const save = useMemo(
    () => debounce((v: VideoComposition, seq: number) => void doSave(v, seq), AUTOSAVE_MS),
    [doSave]
  )
  // Bind the late-declared re-arm hook (see rearmPendingSave above): only re-arms when a save
  // is actually pending, and reuses the CURRENT edit seq (the merge adds no new edit).
  rearmPendingSave.current = (doc: VideoComposition) => {
    if (hasUnsavedEdits()) save(doc, editSeq.current)
  }

  // ── Whole-reel undo/redo. The video composer's edits live on the VideoComposition (beat ops,
  // clip drags, audio/caption settings) — NOT on the per-beat overlay editor, whose own stack is
  // wiped on every view resync. So history is kept HERE, at the document level: every commitVideo
  // pushes one step, and drag gestures (timeline + canvas) bracket themselves so a 200-move drag
  // is ONE step. The context exposes a wrapped editor whose undo/redo point at THIS stack, which
  // is what the editor bar + Cmd+Z drive. ──
  // Stacks live in refs; this tick re-renders canUndo/canRedo when a push happens OUTSIDE a
  // state update (endHistoryGesture after a drag lands in refs only — without it, a drag as
  // the FIRST edit would leave the Undo button disabled until something else re-rendered).
  // The VALUE is kept (not just the setter) so wrappedEditor can depend on it — a re-render
  // alone never recomputes a memo, so without the dep a ref-only push could serve stale
  // canUndo/canRedo from the cached editor object.
  const [historyTick, forceHistoryTick] = useState(0)
  const pushHistory = useCallback((snapshot: VideoComposition) => {
    videoPast.current.push(snapshot)
    if (videoPast.current.length > VIDEO_MAX_HISTORY) videoPast.current.shift()
    videoFuture.current = []
    forceHistoryTick(n => n + 1)
  }, [])
  const clearHistory = useCallback(() => {
    videoPast.current = []
    videoFuture.current = []
    historyGestureSnap.current = null
    historyGestureActive.current = false
    forceHistoryTick(n => n + 1)
  }, [])
  const beginHistoryGesture = useCallback(() => {
    if (historyGestureActive.current) return
    historyGestureActive.current = true
    historyGestureSnap.current = videoRef.current
  }, [])
  const endHistoryGesture = useCallback(() => {
    if (!historyGestureActive.current) return
    historyGestureActive.current = false
    const snap = historyGestureSnap.current
    historyGestureSnap.current = null
    if (!snap) return
    if (JSON.stringify(snap) !== JSON.stringify(videoRef.current)) pushHistory(snap)
  }, [pushHistory])

  // Persist a mutated timeline: keep our ref fresh, update state, and autosave (unless previewing).
  // NOTE: this persists the composition ONLY. It deliberately never triggers a master render.
  const adoptDoc = useCallback(
    (next: VideoComposition) => {
      videoRef.current = next
      setVideo(next)
      if (previewingRef.current == null) {
        editSeq.current += 1
        save(next, editSeq.current)
        setEditedSinceRender(true)
      }
    },
    [save]
  )
  // Every USER edit funnels through here → one undo step each (gestures coalesce via the
  // bracket above; server-state adoptions — hydration, voice synth, post-revise refresh —
  // bypass commitVideo on purpose and never enter history).
  const commitVideo = useCallback(
    (next: VideoComposition) => {
      if (!historyGestureActive.current && previewingRef.current == null) {
        pushHistory(videoRef.current)
      }
      adoptDoc(next)
    },
    [adoptDoc, pushHistory]
  )

  // Undo/redo restore a snapshot with meta.dirty STAMPED — same rule as the static editor
  // (use-composition-editor): a clean pre-edit snapshot would never autosave, leaving the
  // server on the post-edit doc, which resurrects the undone edit on any refetch.
  const videoUndo = useCallback(() => {
    const prev = videoPast.current.pop()
    if (!prev) return
    videoFuture.current.push(videoRef.current)
    const doc = { ...prev, meta: { ...prev.meta, dirty: true } }
    adoptDoc(doc)
    const i = Math.max(0, Math.min(activeRef.current, doc.beats.length - 1))
    setActiveIndex(i)
    syncView(videoEditorViewDoc(doc, i))
  }, [adoptDoc, syncView])
  const videoRedo = useCallback(() => {
    const nxt = videoFuture.current.pop()
    if (!nxt) return
    videoPast.current.push(videoRef.current)
    const doc = { ...nxt, meta: { ...nxt.meta, dirty: true } }
    adoptDoc(doc)
    const i = Math.max(0, Math.min(activeRef.current, doc.beats.length - 1))
    setActiveIndex(i)
    syncView(videoEditorViewDoc(doc, i))
  }, [adoptDoc, syncView])

  // Reconcile an active-beat VIEW edit back into the deck clips + autosave the whole video.
  // Gated on the view's own dirty flag, so a replaceComposition (beat switch / AI refresh,
  // which lands dirty:false) never triggers a write-back loop. Rules, by layer id against the
  // clips whose window intersects the active beat's span:
  //   • id in both → the clip keeps its window, its layer becomes the edited layer;
  //   • id only in the editor (Add text/shape/photo) → a NEW clip spanning the active beat;
  //   • id only in the clips (deleted in the editor) → the clip is removed.
  useEffect(() => {
    if (!editor.comp.meta.dirty) return
    const i = activeRef.current
    const cur = videoRef.current
    const span = videoBeatSpans(cur.beats)[i]
    if (!span) return
    const editorLayers = editor.comp.layers
    // Membership is judged against the ids the view was SYNTHESIZED with (viewIdsRef), never
    // the live span: a clip the user dragged into the active span since the last view sync
    // must not read as "deleted in the editor", and one dragged out must still take edits.
    const viewIds = viewIdsRef.current
    let changed = false
    const nextClips: VideoLayerClip[] = []
    const knownIds = new Set<string>()
    for (const c of clipsOf(cur)) {
      knownIds.add(c.layer.id)
      const el = editorLayers.find(l => l.id === c.layer.id)
      if (!el) {
        if (viewIds.has(c.layer.id)) {
          changed = true // was in the view, gone from the editor → deleted
          continue
        }
        nextClips.push(c) // never in the view → untouched
        continue
      }
      if (JSON.stringify(el) !== JSON.stringify(c.layer)) {
        nextClips.push({ ...c, layer: el })
        changed = true
      } else {
        nextClips.push(c)
      }
    }
    for (const l of editorLayers) {
      if (!knownIds.has(l.id)) {
        nextClips.push({ layer: l, start: span.start, end: span.end })
        viewIds.add(l.id)
        changed = true
      }
    }
    if (!changed) return
    commitVideo({ ...cur, layers: nextClips, meta: { ...cur.meta, dirty: true } })
  }, [editor.comp, commitVideo])

  const selectBeat = useCallback(
    (i: number) => {
      if (i === activeRef.current || i < 0 || i >= videoRef.current.beats.length) return
      setActiveIndex(i)
      syncView(videoEditorViewDoc(videoRef.current, i))
    },
    [syncView]
  )

  // Apply a timeline op, then re-sync the active beat + the view editor to `nextActive`.
  const applyVideoOp = useCallback(
    (next: VideoComposition, nextActive: number) => {
      const clamped = Math.max(0, Math.min(nextActive, next.beats.length - 1))
      commitVideo(next)
      setActiveIndex(clamped)
      syncView(videoEditorViewDoc(next, clamped))
    },
    [commitVideo, replaceComposition]
  )

  // The explicit render — the ONLY master-render trigger. The reel composite is MINUTES long, so
  // the server kicks it off in the BACKGROUND and returns immediately (a synchronous request would
  // die at the ingress timeout — the old false "couldn't render"). We flush edits, POST to start it,
  // and keep `rendering` until the new reel lands: the videoSig watcher below clears it + refreshes
  // the timeline when creative:content:updated (new videoUrl) arrives; a timeout is the safety net.
  const renderTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const RENDER_TIMEOUT_MS = 12 * 60 * 1000
  // The edit seq the in-flight render CONTAINS — the completion watcher compares against the
  // current seq so edits made during the render window (even saved ones) keep the stale hint.
  const renderedSeqRef = useRef(0)
  const render = useCallback(async () => {
    setRenderError(null)
    setRendering(true)
    sawRenderGenerating.current = false
    renderedSeqRef.current = editSeq.current
    if (renderTimer.current) clearTimeout(renderTimer.current)
    renderTimer.current = setTimeout(() => {
      setRendering(false)
      setRenderError('Render is taking longer than usual. Check back shortly.')
    }, RENDER_TIMEOUT_MS)
    try {
      save.flush()
      if (inFlightSave.current) await inFlightSave.current
      if (hasUnsavedEdits()) await doSave(videoRef.current, editSeq.current)
      // doSave swallows its own rejection (the saveFailed flag) — NEVER render a stale server
      // doc while claiming the latest edits are in it. Abort loudly; the user can retry.
      if (hasUnsavedEdits()) {
        throw new Error(
          'Your latest edits could not be saved. Check your connection and try again.'
        )
      }
      // Returns fast ("rendering" started). Completion arrives via the videoSig watcher (Pusher
      // creative:content:updated → new videoUrl), which clears `rendering` + refetches the timeline.
      await renderVideoComposition(creativeId)
    } catch (err) {
      console.error('video composition render failed:', err)
      if (renderTimer.current) clearTimeout(renderTimer.current)
      setRenderError("Couldn't start the render. Try again.")
      setRendering(false)
    }
  }, [creativeId, save, doSave, hasUnsavedEdits])
  useEffect(
    () => () => {
      if (renderTimer.current) clearTimeout(renderTimer.current)
    },
    []
  )

  // ── Per-beat clip generation (K4/K5): call the async server endpoint, then poll the composition
  // until THIS beat's clip lands, and apply it by id (index-independent + edit-safe). Lives in the
  // provider so it survives the inspector unmounting during the 1-2 min generation. ──
  const [generatingBeatIds, setGeneratingBeatIds] = useState<Set<string>>(new Set())
  // Identity for a clip source across ALL kinds: the server's applyClipToBeat stores the
  // rehosted winner as {kind:'attachment'} (the NORMAL success path) — a url/dataUri-only
  // check made the poll below blind to every successful regenerate.
  const srcUrl = (
    s: { kind: string; url?: string; dataUri?: string; attachmentId?: string } | null | undefined
  ): string =>
    s?.kind === 'attachment'
      ? (s.attachmentId ?? '')
      : s?.kind === 'url'
        ? (s.url ?? '')
        : s?.kind === 'dataUri'
          ? (s.dataUri ?? '')
          : ''
  const regenerateBeatBackground = useCallback(
    async (
      beatId: string,
      body: { shotKind: 't2v' | 'i2v'; sceneIdea?: string; imageAttachmentId?: string }
    ) => {
      const cur = videoRef.current.beats.find(b => b.id === beatId)
      const prevUrl = cur && cur.background.kind === 'video' ? srcUrl(cur.background.clipSrc) : ''
      setGeneratingBeatIds(s => new Set(s).add(beatId))
      try {
        await generateBeatClip(creativeId, { beatId, ...body })
        const deadline = Date.now() + 4 * 60 * 1000
        for (;;) {
          // Stop polling when the provider unmounted (navigated to another creative): a live
          // loop here would keep fetching and could commit a doc built from a FROZEN videoRef
          // snapshot over whatever was persisted since.
          if (unmountedRef.current) break
          if (Date.now() > deadline) break
          await new Promise(r => setTimeout(r, 5000))
          if (unmountedRef.current) break
          const res = await fetchComposition(creativeId).catch(() => null)
          const comp = res?.composition
          if (!comp || !isVideoComposition(comp)) continue
          const fresh = comp.beats.find(b => b.id === beatId)
          if (!fresh || fresh.background.kind !== 'video') continue
          const newUrl = srcUrl(fresh.background.clipSrc)
          if (newUrl && newUrl !== prevUrl) {
            const v = videoRef.current
            const idx = v.beats.findIndex(b => b.id === beatId)
            if (idx >= 0) {
              const next = opSetBeatBackground(v, idx, fresh.background)
              if (next !== v) commitVideo(next)
              if (idx === activeRef.current) {
                syncView(videoEditorViewDoc(next, idx))
              }
            }
            break
          }
        }
      } catch (err) {
        console.error('beat clip regenerate failed:', err)
      } finally {
        setGeneratingBeatIds(s => {
          const n = new Set(s)
          n.delete(beatId)
          return n
        })
      }
    },
    [creativeId, commitVideo, replaceComposition]
  )

  // Regenerate the music bed (optionally steered by a prompt). Fire-and-poll like the beat
  // clips: the server answers { generating } immediately and the fresh track lands on
  // composition.music.src minutes later.
  const [musicGenerating, setMusicGenerating] = useState(false)
  const musicSrcId = useCallback(
    (s: ImageSrc | null | undefined): string =>
      s?.kind === 'attachment'
        ? s.attachmentId
        : s?.kind === 'url'
          ? s.url
          : s?.kind === 'dataUri'
            ? s.dataUri
            : '',
    []
  )
  const regenerateMusic = useCallback(
    async (prompt?: string) => {
      const localStartId = musicSrcId(videoRef.current.music?.src)
      setMusicGenerating(true)
      try {
        // Baseline from the SERVER doc, not the local one: a locally-swapped track whose
        // autosave is still in flight would otherwise make the first poll tick a false
        // positive (server's OLD bed !== local id) and commit stale audio.
        const base = await fetchComposition(creativeId).catch(() => null)
        // Baseline-fetch failure must NOT leave baseId null: the first poll tick would then
        // read the server's UNCHANGED bed as "fresh" (old !== null), commit the old track as
        // an edit and clear the composing state minutes early. The local id is the honest
        // fallback baseline.
        const baseId =
          base?.composition && isVideoComposition(base.composition)
            ? musicSrcId(base.composition.music?.src)
            : localStartId
        await regenerateReelMusic(creativeId, prompt)
        const deadline = Date.now() + 5 * 60 * 1000
        for (;;) {
          if (unmountedRef.current || Date.now() > deadline) break
          await new Promise(r => setTimeout(r, 5000))
          if (unmountedRef.current) break
          const res = await fetchComposition(creativeId).catch(() => null)
          const comp = res?.composition
          if (!comp || !isVideoComposition(comp)) continue
          const freshSrc = comp.music?.src
          if (!freshSrc || musicSrcId(freshSrc) === baseId) continue
          // Choices made WHILE composing win: a track the user swapped meanwhile stays; a
          // muted bed stays muted (the fresh track still locks in as its src).
          if (musicSrcId(videoRef.current.music?.src) !== localStartId) break
          const v = videoRef.current
          commitVideo(opSetMusic(v, { enabled: v.music?.enabled ?? true, src: freshSrc }))
          break
        }
      } catch (err) {
        console.error('music regenerate failed:', err)
      } finally {
        setMusicGenerating(false)
      }
    },
    [creativeId, commitVideo, musicSrcId]
  )

  // ── Live inline playback (K2) — one rAF clock is the single source of truth for the playhead.
  // N-series: the clock runs on EFFECTIVE (master) time, so the preview's length + beat
  // boundaries match the mp4 exactly (crossfade overlaps subtracted). ──
  const totalDuration = videoTotalDuration(video.beats)
  const totalRef = useRef(totalDuration)
  totalRef.current = totalDuration
  const [playing, setPlaying] = useState(false)
  // The playhead lives in a ref + subscriber set (NOT state): a 60fps setState here made
  // every context consumer re-render for the whole playback. Subscribers (stage, playhead
  // line, timecode) get notified per tick and re-render alone via useReelPlayhead.
  const currentTimeRef = useRef(0)
  const timeListeners = useRef(new Set<() => void>())
  const setPlayhead = useCallback((t: number) => {
    currentTimeRef.current = t
    for (const cb of timeListeners.current) cb()
  }, [])
  const getCurrentTime = useCallback(() => currentTimeRef.current, [])
  const subscribeTime = useCallback((cb: () => void) => {
    timeListeners.current.add(cb)
    return () => {
      timeListeners.current.delete(cb)
    }
  }, [])
  const rafRef = useRef<number | null>(null)
  const lastTsRef = useRef<number>(0)

  const beatIndexAtTime = useCallback(
    (t: number): number => beatIndexAt(videoRef.current.beats, t),
    []
  )

  const stopClock = useCallback(() => {
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current)
    rafRef.current = null
  }, [])

  const pause = useCallback(() => {
    stopClock()
    setPlaying(false)
    // Land the editable selection on the beat under the playhead so editing resumes where you stopped.
    const i = beatIndexAtTime(currentTimeRef.current)
    if (i !== activeRef.current) selectBeat(i)
  }, [stopClock, beatIndexAtTime, selectBeat])

  const tick = useCallback(
    (ts: number) => {
      const last = lastTsRef.current || ts
      const dt = (ts - last) / 1000
      lastTsRef.current = ts
      const next = currentTimeRef.current + dt
      if (next >= totalRef.current) {
        setPlayhead(totalRef.current)
        stopClock()
        setPlaying(false)
        return
      }
      setPlayhead(next)
      rafRef.current = requestAnimationFrame(tick)
    },
    [stopClock, setPlayhead]
  )

  // ── VO on demand (P1): the live preview SPEAKS before any render. Play auto-synthesizes
  // every missing/stale voice (free, cached as a durable voSrc + word-timed captions); the VO
  // context offers the same per beat. Fire-and-forget: playback starts silent and each voice
  // drops in as it lands (a beat already playing picks it up on its next loop/mount). ──
  const [voicingBeatIds, setVoicingBeatIds] = useState<Set<string>>(new Set())
  const voicingRef = useRef<Set<string>>(new Set())
  const voiceBeat = useCallback(
    (beatId: string) => {
      const beat = videoRef.current.beats.find(b => b.id === beatId)
      const script = beat?.vo?.trim() ?? ''
      if (!beat || !script) return
      if (!beat.voDirty && beat.voSrc) return // fresh take already recorded
      if (voicingRef.current.has(beatId)) return
      voicingRef.current.add(beatId)
      setVoicingBeatIds(s => new Set(s).add(beatId))
      // Send the CURRENT voice explicitly: right after a voice switch the debounced autosave
      // may not have landed, so the server's stored voVoiceId can be a beat behind.
      void synthBeatVo(creativeId, beatId, videoRef.current.voVoiceId)
        .then(res => {
          // Apply by id WITHOUT an autosave echo (the server already persisted it). Skip if
          // the script changed while recording — the user's new words win.
          const cur = videoRef.current
          const beat = cur.beats.find(b => b.id === beatId)
          if (!beat || (beat.vo?.trim() ?? '') !== script) return
          const adopt = (doc: VideoComposition): VideoComposition => {
            const target = doc.beats.find(b => b.id === beatId)
            if (!target || (target.vo?.trim() ?? '') !== script) return doc
            return {
              ...doc,
              beats: doc.beats.map(b =>
                b.id === beatId
                  ? { ...b, voSrc: res.voSrc, captions: res.captions, voDirty: false }
                  : b
              ),
            }
          }
          // Undo snapshots holding the pre-voice beat would resurrect voDirty + a stale take.
          rebaseHistory(adopt)
          const next = adopt(cur)
          videoRef.current = next
          setVideo(next)
          // If a debounced autosave is pending, it holds a PRE-voice snapshot — re-arm it with
          // the merged doc so the whole-document PUT can't clobber the server's voSrc/captions.
          rearmPendingSave.current(next)
        })
        .catch(err => console.warn('beat VO synth failed:', err))
        .finally(() => {
          voicingRef.current.delete(beatId)
          setVoicingBeatIds(s => {
            const n = new Set(s)
            n.delete(beatId)
            return n
          })
        })
    },
    [creativeId, rebaseHistory]
  )
  const ensureVoices = useCallback(() => {
    for (const b of videoRef.current.beats) {
      if ((b.vo?.trim() ?? '') && (b.voDirty || !b.voSrc)) voiceBeat(b.id)
    }
  }, [voiceBeat])

  const play = useCallback(() => {
    // Snapshot edits first so what plays matches what's saved.
    save.flush()
    // Kick off any missing voices so the preview speaks (each lands mid-playback).
    ensureVoices()
    // Restart from 0 if we're parked at the end.
    if (currentTimeRef.current >= totalRef.current - 1e-3) {
      setPlayhead(0)
    }
    lastTsRef.current = 0
    setPlaying(true)
    stopClock()
    rafRef.current = requestAnimationFrame(tick)
  }, [save, stopClock, tick, ensureVoices])

  const seek = useCallback(
    (t: number) => {
      stopClock()
      setPlaying(false)
      const clamped = Math.max(0, Math.min(t, totalRef.current))
      setPlayhead(clamped)
      const i = beatIndexAtTime(clamped)
      if (i !== activeRef.current) selectBeat(i)
    },
    [stopClock, beatIndexAtTime, selectBeat, setPlayhead]
  )

  // Stop the clock on unmount.
  useEffect(() => () => stopClock(), [stopClock])
  // If the timeline shrinks below the playhead (delete/trim), clamp it back in range.
  useEffect(() => {
    if (currentTimeRef.current > totalDuration) {
      setPlayhead(totalDuration)
    }
  }, [totalDuration, setPlayhead])

  const videoValue = useMemo<VideoTimelineContextValue>(
    () => ({
      beats: video.beats,
      activeIndex,
      selectBeat,
      addBeat: () => {
        const next = opAddBeat(videoRef.current)
        if (next === videoRef.current) return null
        applyVideoOp(next, next.beats.length - 1)
        return next.beats[next.beats.length - 1]!.id
      },
      duplicateBeat: (i: number) => {
        const next = opDuplicateBeat(videoRef.current, i)
        // Follow the beat you were editing: a copy inserted at i+1 shifts anything at/after i+1
        // down by one. Duplicating the ACTIVE beat selects the new copy.
        if (next !== videoRef.current) {
          const active = activeRef.current
          applyVideoOp(next, i === active ? i + 1 : active > i ? active + 1 : active)
        }
      },
      deleteBeat: (i: number) => {
        const next = opDeleteBeat(videoRef.current, i)
        // Deleting a beat BEFORE the active one shifts it down; deleting the active (or later) clamps.
        if (next !== videoRef.current) {
          const active = activeRef.current
          applyVideoOp(next, active > i ? active - 1 : active < i ? active : i > 0 ? i - 1 : 0)
        }
      },
      moveBeat: (from: number, to: number) => {
        const next = opMoveBeat(videoRef.current, from, to)
        // Remap the active index through the move so reordering a NON-active beat doesn't steal
        // focus; moving the active beat follows it to `to`.
        if (next !== videoRef.current) {
          const active = activeRef.current
          const remapped =
            active === from
              ? to
              : from < active && active <= to
                ? active - 1
                : to <= active && active < from
                  ? active + 1
                  : active
          applyVideoOp(next, remapped)
        }
      },
      trimBeat: (i: number, seconds: number) => {
        const next = opTrimBeat(videoRef.current, i, seconds)
        if (next !== videoRef.current) commitVideo(next)
      },
      setBeatVO: (i: number, vo: string) => {
        const next = opSetBeatVO(videoRef.current, i, vo)
        if (next !== videoRef.current) commitVideo(next)
      },
      setBeatBackground: (i: number, bg: BeatBackground) => {
        const next = opSetBeatBackground(videoRef.current, i, bg)
        if (next !== videoRef.current) commitVideo(next)
      },
      setBeatBackgroundById: (beatId: string, bg: BeatBackground) => {
        const i = videoRef.current.beats.findIndex(b => b.id === beatId)
        if (i < 0) return
        const next = opSetBeatBackground(videoRef.current, i, bg)
        if (next !== videoRef.current) commitVideo(next)
      },
      setBeatTransition: (i: number, t: 'cut' | 'crossfade') => {
        const next = opSetBeatTransition(videoRef.current, i, t)
        if (next !== videoRef.current) commitVideo(next)
      },
      clips: clipsOf(video),
      spans: videoBeatSpans(video.beats),
      // Window-only retimes never resync the view editor (windows aren't in the view doc), so
      // a drag can't nuke the selection mid-gesture. Content-set changes (remove/duplicate/z)
      // DO resync, exactly like beat ops.
      moveClip: (layerId: string, newStart: number) => {
        const next = opMoveClip(videoRef.current, layerId, newStart)
        if (next !== videoRef.current) commitVideo(next)
      },
      resizeClip: (layerId: string, edge: 'start' | 'end', t: number) => {
        const next = opResizeClip(videoRef.current, layerId, edge, t)
        if (next !== videoRef.current) commitVideo(next)
      },
      setClipWindow: (layerId: string, start: number, end: number) => {
        const next = opSetClipWindow(videoRef.current, layerId, start, end)
        if (next !== videoRef.current) commitVideo(next)
      },
      setClipAnim: (
        layerId: string,
        dir: 'in' | 'out',
        preset: 'fade' | 'slide' | 'pop' | null
      ) => {
        const next = opSetClipAnim(videoRef.current, layerId, dir, preset)
        if (next !== videoRef.current) applyVideoOp(next, activeRef.current)
      },
      removeClip: (layerId: string) => {
        const next = opRemoveClip(videoRef.current, layerId)
        if (next !== videoRef.current) applyVideoOp(next, activeRef.current)
      },
      duplicateClip: (layerId: string) => {
        const { video: next } = opDuplicateClip(videoRef.current, layerId)
        if (next !== videoRef.current) applyVideoOp(next, activeRef.current)
      },
      moveClipZ: (layerId: string, dir: 'up' | 'down') => {
        const next = opMoveClipZ(videoRef.current, layerId, dir)
        if (next !== videoRef.current) applyVideoOp(next, activeRef.current)
      },
      beginHistoryGesture,
      endHistoryGesture,
      regenerateBeatBackground,
      generatingBeatIds,
      voicingBeatIds,
      voiceBeat,
      hydration,
      bringToLife,
      toggleMusic: () => {
        const cur = videoRef.current
        const enabled = !cur.music?.enabled
        commitVideo(opSetMusic(cur, { ...cur.music, enabled }))
      },
      musicEnabled: !!video.music?.enabled,
      musicSrc: video.music?.src ?? null,
      setMusicTrack: (src: ImageSrc) => {
        commitVideo(opSetMusic(videoRef.current, { enabled: true, src }))
      },
      regenerateMusic,
      musicGenerating,
      voEnabled: video.voEnabled !== false,
      setVoEnabled: (on: boolean) => {
        const cur = videoRef.current
        if ((cur.voEnabled !== false) === on) return
        commitVideo({ ...cur, voEnabled: on, meta: { ...cur.meta, dirty: true } })
      },
      voVoiceId: video.voVoiceId ?? null,
      setVoice: (voiceId: string) => {
        const next = opSetVoVoiceId(videoRef.current, voiceId)
        if (next !== videoRef.current) commitVideo(next)
      },
      captionsEnabled: video.captionsEnabled !== false,
      setCaptionsEnabled: (on: boolean) => {
        const cur = videoRef.current
        if ((cur.captionsEnabled !== false) === on) return
        commitVideo({ ...cur, captionsEnabled: on, meta: { ...cur.meta, dirty: true } })
      },
      captionPlacement: video.captionPlacement ?? 'bottom',
      setCaptionPlacement: (p: 'top' | 'center' | 'bottom') => {
        const cur = videoRef.current
        if ((cur.captionPlacement ?? 'bottom') === p) return
        commitVideo({ ...cur, captionPlacement: p, meta: { ...cur.meta, dirty: true } })
      },
      captionStyle: video.captionStyle ?? 'pop',
      setCaptionStyle: (s: 'pop' | 'clean' | 'block') => {
        const cur = videoRef.current
        if ((cur.captionStyle ?? 'pop') === s) return
        commitVideo({ ...cur, captionStyle: s, meta: { ...cur.meta, dirty: true } })
      },
      canAdd: video.beats.length < MAX_BEATS,
      canDelete: video.beats.length > MIN_BEATS,
      render,
      rendering,
      renderError,
      renderPhase,
      editedSinceRender,
      totalDuration,
      getCurrentTime,
      subscribeTime,
      playing,
      play,
      pause,
      seek,
    }),
    [
      video,
      activeIndex,
      selectBeat,
      applyVideoOp,
      commitVideo,
      beginHistoryGesture,
      endHistoryGesture,
      render,
      rendering,
      renderError,
      renderPhase,
      editedSinceRender,
      regenerateBeatBackground,
      generatingBeatIds,
      voicingBeatIds,
      voiceBeat,
      hydration,
      bringToLife,
      regenerateMusic,
      musicGenerating,
      totalDuration,
      getCurrentTime,
      subscribeTime,
      playing,
      play,
      pause,
      seek,
    ]
  )

  // Flush pending save on unmount + before unload (no edit lost). NO master re-render here — the
  // reel is rendered ONLY on the explicit render() above.
  useEffect(() => {
    const flush = () => save.flush()
    window.addEventListener('beforeunload', flush)
    return () => {
      window.removeEventListener('beforeunload', flush)
      save.flush()
    }
  }, [save])

  // Post-revise live refresh: an AI reviseReel swaps the reel's video (its "master"). When it
  // changes and we have no unsaved edits (a real revise flushes first), refetch the fresh timeline
  // and reset the editor so the canvas shows the AI's rewrite (with merged edits) — the same
  // hasUnsavedEdits gate the static/carousel providers use to avoid clobbering in-memory work.
  const videoSig = useCreativeStore(s => {
    const content = s.creatives.find(c => c.id === creativeId)?.content
    if (!content) return ''
    const vId = 'videoAttachmentId' in content ? content.videoAttachmentId : undefined
    const vUrl = 'videoUrl' in content ? content.videoUrl : undefined
    return String(vId || vUrl || '')
  })
  const knownSig = useRef<string | null>(null)
  useEffect(() => {
    if (knownSig.current === null) {
      knownSig.current = videoSig
      return
    }
    if (!videoSig || videoSig === knownSig.current) return
    if (previewingRef.current != null) return
    // The reel's video changed → an explicit Render (or an AI revise) just landed. Clear the
    // rendering state + its safety timeout so the dock's "Rendering…" button resets to "Render".
    const wasExplicitRender = renderingRef.current
    if (renderTimer.current) clearTimeout(renderTimer.current)
    setRendering(false)
    setRenderError(null)
    setRenderPhase(null)
    if (hasUnsavedEdits()) return
    // A fresh render (or AI revise) just landed → the score matches the timeline again ONLY if
    // no edits happened since the render was fired (saved-or-not — the reel doesn't have them).
    setEditedSinceRender(editSeq.current !== renderedSeqRef.current)
    knownSig.current = videoSig
    let cancelled = false
    fetchComposition(creativeId)
      .then(res => {
        if (cancelled || !res.composition || !isVideoComposition(res.composition)) return
        const fresh = normalizeVideoComposition(res.composition)
        clearHistory() // a wholesale AI/server document — undoing across it would resurrect pre-revise docs
        videoRef.current = fresh
        setVideo(fresh)
        const i = Math.max(0, Math.min(activeRef.current, fresh.beats.length - 1))
        setActiveIndex(i)
        syncView(videoEditorViewDoc(fresh, i))
        // For an AI revise the refetched doc IS what the fresh master rendered (the server
        // merges edits) — the score is current for it. For an EXPLICIT render, the master was
        // built from the doc captured at render start: a mid-render edit is in the refetched
        // doc but NOT in the master, so the staleness computed above must stand.
        if (!wasExplicitRender) {
          renderedSeqRef.current = editSeq.current
          setEditedSinceRender(false)
        }
      })
      .catch(err => console.warn('post-revise video refresh failed:', err))
    return () => {
      cancelled = true
    }
  }, [videoSig, creativeId, replaceComposition, hasUnsavedEdits, clearHistory])

  // The context editor with undo/redo REPOINTED at the whole-reel history (the overlay editor's
  // own stack is wiped on every view resync, so it can't be the composer's undo). Canvas layer
  // drags flip the view dirty on EVERY pointer move and each reconcile tick commits — bracket
  // them so a drag lands as one step. canUndo/canRedo read the refs at render time: every push
  // happens inside commitVideo, whose setVideo re-renders this provider.
  const wrappedEditor = useMemo(
    () => ({
      ...editor,
      undo: videoUndo,
      redo: videoRedo,
      canUndo: videoPast.current.length > 0,
      canRedo: videoFuture.current.length > 0,
      beginGesture: () => {
        beginHistoryGesture()
        editor.beginGesture()
      },
      endGesture: () => {
        editor.endGesture()
        endHistoryGesture()
      },
    }),
    // historyTick isn't read in the body but IS the signal that the ref-backed stacks changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, videoUndo, videoRedo, beginHistoryGesture, endHistoryGesture, historyTick]
  )

  const value = useMemo<CompositionEditorContextValue>(
    () => ({
      editor: wrappedEditor,
      creativeId,
      saving,
      saveFailed,
      dock: { open: dockOpen, setOpen: setDockOpen, openDock: () => setDockOpen(true) },
      flushSave: async () => {
        save.flush()
        if (inFlightSave.current) await inFlightSave.current
        // A prior save may have FAILED (savedSeq still behind). One more synchronous attempt
        // so a chat revise merges the user's latest edits, not a stale snapshot — mirrors the
        // static provider's flushSave.
        if (hasUnsavedEdits()) await doSave(videoRef.current, editSeq.current)
      },
      previewSeq,
      previewVersion: async (seq: number) => {
        if (previewingRef.current === seq) return
        if (previewingRef.current == null) {
          save.flush()
          if (inFlightSave.current) await inFlightSave.current
        }
        const detail = await useVersionsStore.getState().fetchDetail(creativeId, seq)
        if (!detail?.composition || !isVideoComposition(detail.composition)) return
        previewingRef.current = seq
        setPreviewSeq(seq)
        clearHistory()
        const v = normalizeVideoComposition(detail.composition)
        videoRef.current = v
        setVideo(v)
        setActiveIndex(0)
        syncView(videoEditorViewDoc(v, 0))
      },
      exitPreview: () => {
        if (previewingRef.current == null) return
        previewingRef.current = null
        setPreviewSeq(null)
        fetchComposition(creativeId)
          .then(res => {
            if (!res?.composition || !isVideoComposition(res.composition)) return
            clearHistory()
            const v = normalizeVideoComposition(res.composition)
            videoRef.current = v
            setVideo(v)
            setActiveIndex(0)
            syncView(videoEditorViewDoc(v, 0))
          })
          .catch(err => console.warn('exit-preview video refresh failed:', err))
      },
      restoreVersion: async (seq: number) => {
        previewingRef.current = null
        setPreviewSeq(null)
        await useVersionsStore.getState().restore(creativeId, seq)
        await useCreativeProcessStore.getState().fetchGeneration(creativeId)
        const res = await fetchComposition(creativeId).catch(() => null)
        if (res?.composition && isVideoComposition(res.composition)) {
          clearHistory()
          const v = normalizeVideoComposition(res.composition)
          videoRef.current = v
          setVideo(v)
          setActiveIndex(0)
          syncView(videoEditorViewDoc(v, 0))
        }
      },
    }),
    [
      wrappedEditor,
      creativeId,
      saving,
      saveFailed,
      dockOpen,
      previewSeq,
      save,
      syncView,
      clearHistory,
    ]
  )

  return (
    <Ctx.Provider value={value}>
      <VideoCtx.Provider value={videoValue}>{children}</VideoCtx.Provider>
    </Ctx.Provider>
  )
}
